import { AnimatePresence, motion } from 'framer-motion';
import {
  Activity,
  AlertCircle,
  BarChart2,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Download,
  Eye,
  FilePlus,
  FileText,
  Layers,
  Moon,
  Network,
  Play,
  Plus,
  Settings,
  Sun,
  Target,
  Trash2,
  Upload,
  User2,
  Users,
  X,
  Zap
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { usePesa } from '../../stores/usePesaEngine';
// ═══════════════════════════════════════════════════════════════════════════════
// 1. THÈMES
// ═══════════════════════════════════════════════════════════════════════════════

const THEMES = {
  light: {
    bg:           'bg-[#f8fafc]',
    bgHex:        '#f8fafc',
    surface:      'bg-white/80',
    surfaceHex:   '#FFFFFF',
    border:       'border-[#e2e8f0]',
    borderHex:    '#e2e8f0',
    text:         'text-[#0f172a]',
    textMuted:    'text-[#64748b]',
    accent:       '#f59e0b',        // ambre
    accentText:   'text-[#f59e0b]',
    accentBg:     'bg-[#f59e0b]/10',
    ink:          '#0f172a',
    panelBg:      'rgba(255,255,255,0.85)',
    panelBorder:  'rgba(245,158,11,0.15)',
    gridLine:     'rgba(15,23,42,0.04)',
    radarBg:      'rgba(245,158,11,0.04)',
    radarBorder:  '#f59e0b',
    headerBg:     'rgba(248,250,252,0.92)',
  },
  dark: {
    bg:           'bg-[#020617]',        // même que C.bg
    bgHex:        '#020617',
    surface:      'bg-[#0f172a]/90',     // slate-900
    surfaceHex:   '#0f172a',
    border:       'border-[#1e293b]',
    borderHex:    '#1e293b',
    text:         'text-[#f1f5f9]',
    textMuted:    'text-[#94a3b8]',
    accent:       '#f59e0b',             // ambre
    accentText:   'text-[#f59e0b]',
    accentBg:     'bg-[#f59e0b]/10',
    ink:          '#ffffff',
    panelBg:      'rgba(15,23,42,0.92)',
    panelBorder:  'rgba(245,158,11,0.15)',
    gridLine:     'rgba(255,255,255,0.03)',
    radarBg:      'rgba(245,158,11,0.04)',
    radarBorder:  '#f59e0b',
    headerBg:     'rgba(2,6,23,0.92)',
  },
};

const useCommercialTheme = (scenario) => {
  const [themeKey, setThemeKey] = useState(scenario?.theme || 'dark');
  const theme = THEMES[themeKey] || THEMES.light;
  const toggle = () => setThemeKey(k => k === 'light' ? 'dark' : 'light');
  return { theme, themeKey, toggle };
};

// ═══════════════════════════════════════════════════════════════════════════════
// 2. DONNÉES PAR DÉFAUT ET TEMPLATES
// ═══════════════════════════════════════════════════════════════════════════════

const EMPTY = {
  slug: '', title: '', pedagogicalGoal: '', displayMode: 'vente', theme: 'dark',
  initialState: { parameters: {}, families: [], interactions: [], agents:[], },
  steps: [],
  simulationEngine: {
    dynamics: [], network: [], quotes: [],
    predictionModel: { enabled: true },
    calibration: { enabled: false },
  },
  solutions: [], counterfactuals: [],
};

const TPL_SERVICE = {
  slug: 'les-voix-du-fleuve-pitch',
  title: 'Les Voix du Fleuve — Pitch de diffusion',
  displayMode: 'vente',
  theme: 'dark',
  metadata: {
    versionEngine: '1.0 Pesacore',
    hasDynamicDebrief: true,
    estimatedDuration: '25 minutes',
    targetAudience: 'Directeur des programmes, Responsable des acquisitions',
    productionYear: 2025,
    genre: 'Documentaire environnemental / portrait humain',
    format: '90 minutes — diffusion prime time',
  },
  initialState: {
    parameters: {
      interet_directeur: 20, conviction_directeur: 10, confiance_directeur: 30,
      securite_financiere: 15, roi_percu: 10, confort_financier: 25,
      alignement_interne: 20, tension_salle: 0, valeur_percue: 30,
      urgence_decision: 0, singularite: 40, probabilite_accord: 15, profil_decision: 0,
    },
    agents: [
      {
        id: 'lambert', name: 'Philippe Lambert', narrativeName: 'Directeur des programmes', type: 'decisionnaire',
        characterTrait: 'Expérimenté, cherche une histoire forte.',
        reactionsTo: {
          poser_question_editoriale: { solo: 'Intérêt +20. Le dialogue est engagé.' },
          lancer_bande_annonce: { solo: 'Intérêt +25, conviction +10. Laisser le silence agir.' },
          montrer_interview_protagoniste: { solo: 'Singularité +20. Le visage de Maria renforce l’authenticité.' },
          presenter_angle_editorial: { solo: 'Conviction +20. Proposer une exclusivité.' },
          proposer_case_prime: { solo: 'Urgence +15. Case précise proposée.' },
          accepter_exclusivite: { solo: 'Urgence +20. Exclusivité en discussion.' },
          signer_accord: { solo: 'Probabilité finalisée. Contrat signé.' },
          marqué_histoire: { solo: 'Le client est touché par Maria. Conviction +15.' },
          marqué_images: { solo: 'Le client apprécie la qualité visuelle. Valeur perçue +10.' },
          reserve: { solo: 'Le client reste réservé. Tension +10.' },
          question_frein: { solo: 'Le client a une objection sur le budget. Continuer avec les chiffres.' },
          question_format: { solo: 'Le client s’interroge sur le format. Adapter le discours.' },
          question_disponibilite: { solo: 'Le client demande la disponibilité. Préparer une réponse précise.' },
          question_retombees: { solo: 'Le client veut des garanties sur les retombées. Montrer le plan de communication.' },
        },
      },
      {
        id: 'dubois', name: 'Marguerite Dubois', narrativeName: 'Responsable financière', type: 'financier',
        characterTrait: 'Prudente, cherche la faille.',
        reactionsTo: {
          montrer_etude_audience: { solo: 'Sécurité +25. Données Médiamétrie.' },
          presenter_budget_previsionnel: { solo: 'Budget 420k€ présenté.' },
          proposer_partage_risques: { solo: 'Alignement +25. Clause de performance.' },
          question_frein: { solo: 'La financière attendait cette question. Sécurité +5.' },
          question_format: { solo: 'La financière prend note de la question de format. Alignement +5.' },
          question_disponibilite: { solo: 'La financière consulte son agenda. Urgence +10.' },
          question_retombees: { solo: 'La financière prend des notes. ROI perçu +10.' },
        },
      },
    ],
    families: [
      { name: 'Direction des programmes', isActive: true, isToxic: false, narrativeRole: 'Décide du positionnement éditorial.', agents: ['lambert'] },
      { name: 'Contrôle financier', isActive: true, isToxic: true, narrativeRole: 'Contrôle le budget d’acquisition.', agents: ['dubois'] },
    ],
    interactions: [
      { source: 'Direction des programmes', target: 'Contrôle financier', type: 'influence', strength: 0.6, evolving: true },
      { source: 'Contrôle financier', target: 'Direction des programmes', type: 'frein', strength: -0.4, evolving: true },
    ],
    theory: {
      primaryConcept: 'Émotion puis chiffres.',
      secondaryConcept: 'Un diffuseur achète une promesse d’audience et un risque calculé.',
      author: 'Marché des documentaires, 2022–2025', year: 2025,
      quote: 'On parie sur une émotion collective.',
      explanation: 'Convaincre par l’histoire, rassurer par les données.',
      quotes: [
        { actionId: 'lancer_bande_annonce', text: 'Les images vraies ont plus de force que les images parfaites.', author: 'Raymond Depardon' },
        { actionId: 'montrer_interview_protagoniste', text: 'Il n’y a pas de documentaire sans un visage.', author: 'Frederick Wiseman' },
        { actionId: 'montrer_etude_audience', text: 'Ce qui ne se mesure pas ne se gère pas – mais ce qui se mesure seulement ne se vit pas.', author: 'Anonyme, industrie TV' },
        { actionId: 'presenter_angle_editorial', text: 'L’angle, c’est le point de vue.', author: 'Chris Marker' },
        { actionId: 'proposer_case_prime', text: 'Le prime time est une promesse faite au spectateur.', author: 'Directeur de programmes, Arte' },
        { actionId: 'accepter_exclusivite', text: 'L’exclusivité, c’est du risque transformé en avantage.', author: 'Économie des médias, 2024' },
        { actionId: 'signer_accord', text: 'La signature, c’est le début – pas la fin.', author: 'Producteur, Festival de Périgueux' },
        { actionId: 'marqué_histoire', text: 'Un visage, une histoire. C’est tout ce dont un documentaire a besoin.', author: 'Frederick Wiseman' },
        { actionId: 'marqué_images', text: 'La beauté visuelle est la première promesse tenue.', author: 'Directeur de la photographie' },
        { actionId: 'reserve', text: 'La réserve est une forme d’attention. Elle mérite le respect.', author: 'Négociateur' },
      ],
      source: {
        title: 'Le marché du documentaire en France — Rapport annuel',
        author: 'Procirep & Angoa', year: 2024,
        url: 'https://procirep.fr/rapport-documentaire',
      },
    },
  },
  steps: [
    // ─── Étape 1 : Premier contact ──────────────────────────────────────
    {
      id: 'premier_contact',
      title: 'Premier contact',
      clientTitle: 'Avant de commencer',
      description: 'Nous engageons la conversation.',
      problematisation: 'Que manque-t-il dans votre grille actuelle ?',
      narrativeOverride: 'Deux décideurs. Quinze minutes. Un projet.',
      narrativeArc: {
        position: 'setup', tensionLevel: 1,
        nextStepForeshadow: 'Vous allez découvrir notre projet.',
      },
      stepSuccessCriteria: {
        minActionsRequired: 1,
        keyActionId: 'poser_question_editoriale',
        feedbackIfMissed: 'Sans question, la présentation reste générique.',
      },
      debriefMessage: 'Premier contact établi.',
      actions: [
        {
          id: 'accepter_cafe',
          label: 'Prendre le temps de s’installer',
          clientLabel: 'Un café d’installation est proposé',
          weight: 1,
          description: 'Un geste simple pour instaurer une atmosphère de travail.',
          effect: { confiance_directeur: 5, tension_salle: -5, profil_decision: 5 },
        },
        {
          id: 'poser_question_editoriale',
          label: 'Poser une question sur leur grille',
          clientLabel: 'Je réponds à la question éditoriale',
          weight: 3,
          description: '« Qu’est-ce qui manque dans votre grille en ce moment ? »',
          effect: { interet_directeur: 20, confiance_directeur: 15, tension_salle: -10, profil_decision: 15 },
        },
      ],
      phases: [
        // Observation : simple confirmation, plus d'input redondant
        {
          type: 'observation',
          duration: 20,
          title: 'Installation',
          narrative: 'Vous êtes en visioconférence avec Philippe Lambert (Directeur des programmes) et Marguerite Dubois (Responsable financière).',
          clientNarrative:
            'Merci d’avoir rejoint cette réunion. L’équipe de production vous présente « Les Voix du Fleuve ». Si vous êtes prêts, nous allons commencer.',
          clientQuestion:
            'Avant d’entrer dans le vif du sujet, souhaitez-vous que l’on aborde un point particulier dès maintenant ?',
          sellerNote:
            'La réponse du diffuseur permet d’ajuster la présentation.',
          advanceLabel: 'Répondre au producteur',
          clientActions: [
            {
              id: 'point_particulier',
              type: 'choice',
              prompt: 'Souhaitez-vous aborder un point particulier ?',
              options: [
                { id: 'demande_oui', label: 'Oui, j’ai une demande spécifique' },
                { id: 'demande_non', label: 'Non, poursuivons' },
              ],
            },
          ],
        },
        // Action : le besoin éditorial est recueilli ici
        {
          type: 'action',
          duration: 30,
          title: 'Premier geste',
          actionIds: ['accepter_cafe', 'poser_question_editoriale'],
          clientActions: [
            {
              id: 'reponse_attentes',
              type: 'input',
              prompt: 'Vous répondez au producteur :',
              fieldLabel: 'Vos attentes ou besoins éditoriaux...',
            },
          ],
          narrative: 'Dialogue avec le directeur des programmes.',
          clientNarrative:
            'Je vous propose un café (virtuel) et nous engageons la discussion. Ma question est simple : selon vous, qu’est-ce qui manque le plus dans votre grille de programmes actuelle ?',
          clientQuestion:
            'Prenez le temps de la réflexion, je vous écoute.',
          sellerNote:
            'Cette réponse peut servir de fil conducteur pour la suite.',
          advanceLabel: 'Poursuivre',
        },
        // Conséquences avec confirmation de prêt
        {
          type: 'consequences',
          duration: 15,
          title: 'Premières réactions',
          narrative: 'Retour du client sur ses attentes.',
          clientNarrative:
            'Très bien, votre retour est précieux. Nous allons maintenant entrer dans le vif du sujet.',
          clientQuestion:
            'Êtes-vous prêt à découvrir le projet ?',
          revealDelta: true,
          advanceLabel: 'Je suis prêt',
          clientActions: [
            {
              id: 'confirmation_pret',
              type: 'choice',
              prompt: 'Confirmez-vous ?',
              options: [
                { id: 'pret_oui', label: 'Oui, je suis curieux' },
                { id: 'pret_non', label: 'Pas encore, une précision...' },
              ],
            },
          ],
        },
      ],
    },

    // ─── Étape 2 : Bande-annonce ──────────────────────────────────────
    {
      id: 'bande_annonce',
      title: 'Bande-annonce',
      clientTitle: 'Deux minutes. Pas une de plus.',
      description: 'Nous lançons la bande-annonce.',
      problematisation: 'Est-ce que ces deux minutes créent un impact durable ?',
      narrativeOverride: 'Silence en salle',
      narrativeArc: {
        position: 'setup', tensionLevel: 2,
        previousStepLink: 'La salle est attentive.',
        nextStepForeshadow: 'Vous allez réagir.',
      },
      stepSuccessCriteria: {
        minActionsRequired: 2,
        keyActionId: 'garder_silence_apres_ba',
        feedbackIfMissed: 'Vous avez parlé trop tôt. L’émotion n’a pas eu le temps de s’installer.',
      },
      debriefMessage: 'Projection terminée.',
      actions: [
        {
          id: 'lancer_bande_annonce',
          label: 'Lancer la bande-annonce',
          clientLabel: 'Je regarde la bande-annonce',
          weight: 3,
          description: 'Deux minutes deux secondes d’images.',
          effect: { interet_directeur: 25, conviction_directeur: 10, valeur_percue: 20, profil_decision: 20 },
          media: {
            type: 'youtube',
            url: 'https://www.youtube.com/watch?v=DYgf4YU2RJc',
            title: 'Bande-annonce officielle',
          },
        },
        {
          id: 'montrer_interview_protagoniste',
          label: 'Montrer l’interview de la protagoniste',
          clientLabel: 'Je découvre l’interview de Maria',
          weight: 2,
          description: 'Deux minutes d’un entretien brut avec Maria.',
          effect: { interet_directeur: 15, singularite: 20, valeur_percue: 10, profil_decision: 15 },
          media: {
            type: 'youtube',
            url: 'https://www.youtube.com/watch?v=DYgf4YU2RJc', // placeholder
            title: 'Interview de Maria',
          },
        },
        {
          id: 'garder_silence_apres_ba',
          label: 'Garder le silence après la projection',
          clientLabel: 'Je prends le temps d’absorber',
          weight: 3,
          description: 'Vous éteignez l’écran et attendez.',
          prerequisites: ['lancer_bande_annonce'],
          effect: { conviction_directeur: 15, confiance_directeur: 10, profil_decision: 15 },
        },
      ],
      phases: [
        {
          type: 'observation',
          duration: 8,
          title: 'Préparation',
          narrative: 'Annonce de la projection au client.',
          clientNarrative:
            'Je vais vous montrer un extrait de deux minutes. Installez-vous confortablement.',
          clientQuestion: 'Prêt ?',
          sellerNote: 'La vidéo peut être lancée une fois le client prêt.',
          advanceLabel: 'Lancer la vidéo',
          clientActions: [
            {
              id: 'confirmation_pret_ba',
              type: 'choice',
              prompt: 'Êtes-vous prêt à visionner ?',
              options: [
                { id: 'pret_ba_oui', label: 'Oui, je suis prêt' },
                { id: 'pret_ba_non', label: 'Attendez un instant...' },
              ],
            },
          ],
        },
        {
          type: 'action',
          duration: 45,
          title: 'Projection',
          actionIds: ['lancer_bande_annonce', 'montrer_interview_protagoniste', 'garder_silence_apres_ba'],
          clientActions: [
            {
              id: 'reaction_ba',
              type: 'choice',
              prompt: 'Qu’est-ce qui vous a le plus marqué ?',
              options: [
                { id: 'marqué_histoire', label: 'L’histoire de Maria', effect: { conviction_directeur: 15 } },
                { id: 'marqué_images',   label: 'La qualité des images', effect: { valeur_percue: 10 } },
                { id: 'reserve',         label: 'Je reste réservé',       effect: { tension_salle: 10 } },
              ],
            },
            {
              id: 'commentaire_ba',
              type: 'input',
              prompt: 'Un commentaire avant de continuer ?',
              fieldLabel: 'Votre commentaire...',
            },
          ],
          narrative: 'Diffusion de l’extrait choisi.',
          clientNarrative:
            'Voici l’extrait. Prenez le temps de le regarder, puis dites‑moi ce qui vous a touché.',
          clientQuestion:
            'Qu’est-ce qui vous a le plus marqué dans ce début ?',
          sellerNote:
            'Laisser la vidéo se dérouler sans interruption préserve son impact.',
          advanceLabel: 'Projection terminée',
        },
        {
          type: 'consequences',
          duration: 20,
          title: 'Votre ressenti',
          narrative: 'Retour du client après la projection.',
          clientNarrative:
            'Merci pour votre retour. Nous allons maintenant approfondir pourquoi ce documentaire mérite une place sur votre antenne.',
          clientQuestion:
            'Souhaitez-vous que je développe un point en particulier avant d’aller plus loin ?',
          revealDelta: true,
          advanceLabel: 'Continuer',
          clientActions: [
            {
              id: 'precision_supplementaire',
              type: 'input',
              prompt: 'Un point à développer ?',
              fieldLabel: 'Votre demande...',
            },
          ],
        },
      ],
    },

    // ─── Étape 3 : Argument éditorial ───────────────────────────────
    {
      id: 'argument_editorial',
      title: 'Argument éditorial',
      clientTitle: 'Pourquoi ce film, pourquoi maintenant ?',
      description: 'Nous présentons l’angle du documentaire.',
      problematisation: 'Qu’est-ce qui rend ce projet unique pour votre chaîne ?',
      narrativeOverride: 'L’émotion ne suffit pas',
      narrativeArc: {
        position: 'escalade', tensionLevel: 3,
        previousStepLink: 'L’émotion a créé l’ouverture.',
        nextStepForeshadow: 'Des questions vont émerger.',
      },
      stepSuccessCriteria: {
        minActionsRequired: 1,
        keyActionId: 'presenter_angle_editorial',
        feedbackIfMissed: 'Sans angle fort, le projet risque d’être difficile à défendre en interne.',
      },
      debriefMessage: 'Argument éditorial présenté.',
      actions: [
        {
          id: 'presenter_angle_editorial',
          label: 'Présenter l’angle en une phrase',
          clientLabel: 'Je retiens l’angle éditorial',
          weight: 3,
          exclusiveGroup: 'argument_cle',
          description: '« Les Voix du Fleuve, c’est l’histoire d’une femme qui se bat pour un fleuve que personne d’autre ne défend. »',
          effect: { conviction_directeur: 20, interet_directeur: 15, singularite: 15, profil_decision: 25 },
        },
        {
          id: 'montrer_comparatif_marche',
          label: 'Montrer le comparatif de marché',
          clientLabel: 'Je consulte le contexte marché',
          weight: 2,
          exclusiveGroup: 'argument_cle',
          description: 'Trois documentaires similaires récents et leur impact.',
          effect: { securite_financiere: 15, roi_percu: 10, profil_decision: 15 },
        },
        {
          id: 'raconter_origine_projet',
          label: 'Raconter l’origine du projet',
          clientLabel: 'Je découvre l’histoire derrière le film',
          weight: 2,
          exclusiveGroup: 'argument_cle',
          description: '« Un réalisateur est resté huit mois sur place. »',
          effect: { confiance_directeur: 20, singularite: 10, profil_decision: 15 },
        },
      ],
      phases: [
        {
          type: 'observation',
          duration: 10,
          title: 'Question du directeur',
          narrative: 'Le directeur des programmes interroge sur la pertinence du projet.',
          clientNarrative:
            'Je comprends que vous ayez besoin de savoir pourquoi ce film, et pourquoi votre chaîne. Laissez-moi vous donner la phrase clé.',
          clientQuestion:
            'Pour vous, qu’est-ce qui fait qu’un documentaire devient incontournable dans une grille ?',
          sellerNote:
            'L’argumentaire peut relier le moment, la chaîne et le public cible.',
          advanceLabel: 'Écouter l’argument',
          clientActions: [
            {
              id: 'definition_incontournable',
              type: 'input',
              prompt: 'D’après vous, un documentaire est incontournable quand...',
              fieldLabel: 'Votre critère principal...',
            },
          ],
        },
        {
          type: 'action',
          duration: 35,
          actionIds: ['presenter_angle_editorial', 'montrer_comparatif_marche', 'raconter_origine_projet'],
          clientActions: [
            {
              id: 'argument_choix_client',
              type: 'choice',
              prompt: 'Quel argument vous semble le plus solide ?',
              options: [
                { id: 'client_angle_editorial', label: 'L\'angle éditorial (l\'histoire de Maria)', effect: { conviction_directeur: 10 } },
                { id: 'client_comparatif', label: 'Le comparatif de marché', effect: { securite_financiere: 10 } },
                { id: 'client_origine', label: 'L\'origine du projet', effect: { singularite: 10 } },
              ],
            },
          ],
          narrative: 'Le vendeur présente les arguments.',
          clientNarrative:
            'Voici trois façons de présenter le projet. Je vous laisse me dire ce qui vous parle le plus.',
          clientQuestion:
            'À votre avis, quel est l’argument le plus percutant pour votre comité de programmes ?',
          sellerNote:
            'Mettre en avant une idée forte est souvent plus efficace.',
          advanceLabel: 'Valider le choix',
        },
        {
          type: 'consequences',
          duration: 15,
          title: 'Effet de l’argument',
          narrative: 'Le client a choisi son angle préféré.',
          clientNarrative:
            'Parfait, nous allons donc approfondir cet axe. Passons maintenant à des éléments plus concrets pour rassurer votre direction financière.',
          clientQuestion:
            'Cela vous convient-il comme base de discussion ?',
          revealDelta: true,
          advanceLabel: 'Passons aux chiffres',
          clientActions: [
            {
              id: 'confirmation_base',
              type: 'choice',
              prompt: 'Cela vous convient ?',
              options: [
                { id: 'convient_oui', label: 'Oui, allons-y' },
                { id: 'convient_non', label: 'J\'aimerais une précision' },
              ],
            },
          ],
        },
      ],
    },

    // ─── Étape 4 : Épreuve des chiffres ─────────────────────────────
    {
      id: 'epreuve_chiffres',
      title: 'Épreuve des chiffres',
      clientTitle: 'Les chiffres ont la parole',
      description: 'Nous présentons les données d’audience et le budget.',
      problematisation: 'Comment évaluer la viabilité financière de ce projet ?',
      narrativeOverride: 'Des questions précises',
      narrativeArc: {
        position: 'pivot', tensionLevel: 4,
        previousStepLink: 'Le directeur est convaincu.',
        nextStepForeshadow: 'Si les chiffres tiennent, l’accord est possible.',
      },
      stepSuccessCriteria: {
        minActionsRequired: 2,
        keyActionId: 'montrer_etude_audience',
        feedbackIfMissed: 'Sans étude, la partie financière peut bloquer.',
      },
      debriefMessage: 'Données présentées.',
      actions: [
        {
          id: 'montrer_etude_audience',
          label: 'Présenter l’étude d’audience',
          clientLabel: 'Je consulte les projections Médiamétrie',
          weight: 3,
          description: '2,3 millions de téléspectateurs potentiels.',
          effect: { securite_financiere: 25, roi_percu: 20, alignement_interne: 10, profil_decision: 25 },
          media: {
            type: 'image',
            url: 'https://via.placeholder.com/900x500?text=Etude+Mediametrie',
            title: 'Projections d’audience Médiamétrie',
          },
        },
        {
          id: 'presenter_plan_retombees',
          label: 'Présenter le plan de retombées',
          clientLabel: 'Je découvre le plan de communication',
          weight: 2,
          description: 'Partenariat presse, festival, campagne digitale.',
          effect: { roi_percu: 15, confort_financier: 20, profil_decision: 15 },
        },
        {
          id: 'presenter_budget_previsionnel',
          label: 'Présenter le budget',
          clientLabel: 'Je prends connaissance du budget',
          weight: 2,
          description: 'Budget total : 420 k€. Demande : 180 k€.',
          effect: { securite_financiere: 20, roi_percu: 10, profil_decision: 20 },
          media: {
            type: 'image',
            url: 'https://via.placeholder.com/900x500?text=Budget+Previsionnel',
            title: 'Budget prévisionnel',
          },
        },
      ],
      phases: [
        {
          type: 'observation',
          duration: 8,
          title: 'Questions financières',
          narrative: 'La responsable financière prend la parole.',
          clientNarrative:
            'Nous entrons dans la partie chiffrée. Voici les projections d’audience réalisées avec Médiamétrie, ainsi que le budget détaillé.',
          clientQuestion:
            'Y a-t-il un élément financier qui vous préoccupe particulièrement avant que je commence ?',
          sellerNote:
            'Les réponses factuelles sont privilégiées ; une information complémentaire pourra être envoyée ultérieurement si nécessaire.',
          advanceLabel: 'Présenter les données',
          clientActions: [
            {
              id: 'preoccupation_financiere',
              type: 'input',
              prompt: 'Une inquiétude particulière ?',
              fieldLabel: 'Élément financier...',
            },
          ],
        },
        {
          type: 'action',
          duration: 40,
          actionIds: ['montrer_etude_audience', 'presenter_plan_retombees', 'presenter_budget_previsionnel'],
          clientActions: [
            {
              id: 'objections_chiffres',
              type: 'choice',
              prompt: 'Quel point souhaitez-vous que je détaille ?',
              options: [
                { id: 'question_frein', label: 'Le budget et sa négociabilité', effect: { securite_financiere: 5, tension_salle: 5 } },
                { id: 'question_format', label: 'L’adaptation du format 90 min', effect: { interet_directeur: 5 } },
                { id: 'question_retombees', label: 'Les garanties de retombées presse', effect: { roi_percu: 10 } },
              ],
            },
            {
              id: 'question_complementaire',
              type: 'input',
              prompt: 'Une autre question sur ces chiffres ?',
              fieldLabel: 'Votre question...',
            },
          ],
          narrative: 'Analyse des chiffres par le client.',
          clientNarrative:
            'Prenez le temps d’examiner ces graphiques. Je reste à votre disposition pour toute clarification.',
          clientQuestion:
            'Parmi ces trois aspects, lequel voulez-vous que je développe en priorité ?',
          sellerNote:
            'Commenter et contextualiser les données renforce leur impact.',
          advanceLabel: 'Les chiffres sont clairs',
        },
        {
          type: 'consequences',
          duration: 15,
          title: 'Arbitrage',
          narrative: 'Discussion sur la viabilité financière.',
          clientNarrative:
            'Merci. Avec ces éléments, je pense que vous avez une vision claire du potentiel de retour sur investissement.',
          clientQuestion:
            'Ces chiffres vous semblent-ils solides pour avancer ?',
          revealDelta: true,
          advanceLabel: 'Aller vers la conclusion',
          clientActions: [
            {
              id: 'confirmation_chiffres',
              type: 'choice',
              prompt: 'Ces chiffres vous rassurent-ils ?',
              options: [
                { id: 'rassure_oui', label: 'Oui, continuons' },
                { id: 'rassure_non', label: 'Je souhaite un complément' },
              ],
            },
          ],
        },
      ],
    },

    // ─── Étape 5 : Négociation et objections ────────────────────────
    {
      id: 'negociation_objections',
      title: 'Négociation et objections',
      clientTitle: 'Discutons des conditions',
      description: 'Nous répondons aux objections et cherchons un accord.',
      problematisation: 'Quelle solution répond le mieux à vos attentes ?',
      narrativeOverride: 'Le moment de décider',
      narrativeArc: {
        position: 'climax', tensionLevel: 5,
        previousStepLink: 'Les deux parties ont les mêmes données.',
        nextStepForeshadow: 'Si un terrain d’entente est trouvé, la signature est proche.',
      },
      stepSuccessCriteria: {
        minActionsRequired: 1,
        keyActionId: 'proposer_partage_risques',
        feedbackIfMissed: 'Sans solution, la réunion peut se terminer sans engagement.',
      },
      debriefMessage: 'Solution proposée.',
      actions: [
        {
          id: 'proposer_case_prime',
          label: 'Proposer une case précise',
          clientLabel: 'Je prends note de la case proposée',
          weight: 2,
          exclusiveGroup: 'solution_objection',
          description: '« Dimanche soir à 20 h 50 en janvier. »',
          effect: { conviction_directeur: 15, urgence_decision: 15, profil_decision: 20 },
        },
        {
          id: 'accepter_exclusivite',
          label: 'Proposer une exclusivité',
          clientLabel: 'J’étudie l’exclusivité',
          weight: 2,
          exclusiveGroup: 'solution_objection',
          description: '« Exclusivité sur 24 mois si signature avant le 15. »',
          effect: { interet_directeur: 20, urgence_decision: 20, profil_decision: 20 },
        },
        {
          id: 'proposer_partage_risques',
          label: 'Proposer un partage de risques',
          clientLabel: 'J’envisage le partage de risques',
          weight: 3,
          exclusiveGroup: 'solution_objection',
          description: '« Si audience > 1,8 M, clause de rediffusion. Sinon, prise en charge de 30 %. »',
          effect: { securite_financiere: 30, alignement_interne: 25, tension_salle: -15, profil_decision: 30 },
        },
      ],
      phases: [
        {
          type: 'observation',
          duration: 10,
          title: 'Objections possibles',
          narrative: 'Le client exprime des réserves.',
          clientNarrative:
            'Je sens qu’il y a peut-être encore quelques hésitations. N’hésitez pas à me les partager, c’est normal à ce stade.',
          clientQuestion:
            'Qu’est-ce qui, aujourd’hui, pourrait vous empêcher de donner votre accord ?',
          sellerNote:
            'Il peut être utile d’écouter les deux interlocuteurs sans prendre parti.',
          advanceLabel: 'Proposer une solution',
          clientActions: [
            {
              id: 'frein_principal',
              type: 'input',
              prompt: 'Votre principal frein :',
              fieldLabel: 'Ce qui vous inquiète...',
            },
          ],
        },
        {
          type: 'action',
          duration: 40,
          actionIds: ['proposer_case_prime', 'accepter_exclusivite', 'proposer_partage_risques'],
          clientActions: [
            {
              id: 'conditions_finales',
              type: 'choice',
              prompt: 'Laquelle de ces conditions vous semble la plus rassurante ?',
              options: [
                { id: 'question_disponibilite', label: 'Une date de diffusion garantie', effect: { urgence_decision: 10 } },
                { id: 'question_frein', label: 'Une flexibilité sur le budget', effect: { securite_financiere: 5, tension_salle: 5 } },
                { id: 'question_retombees', label: 'Un plan de communication détaillé', effect: { roi_percu: 10 } },
              ],
            },
          ],
          narrative: 'Négociation en cours.',
          clientNarrative:
            'Voici plusieurs options que nous pouvons mettre en place pour sécuriser l’opération. Je vous écoute.',
          clientQuestion:
            'Quelle approche vous correspond le mieux ?',
          sellerNote:
            'Le partage de risques peut être présenté en dernier recours.',
          advanceLabel: 'Décider',
        },
        {
          type: 'consequences',
          duration: 20,
          title: 'Vers un accord',
          narrative: 'Un terrain d’entente est trouvé.',
          clientNarrative:
            'Très bien, cette option est tout à fait envisageable pour nous. Nous pouvons maintenant formaliser.',
          clientQuestion:
            'Êtes-vous prêt à passer à l’étape de signature ?',
          revealDelta: true,
          advanceLabel: 'Formaliser',
          clientActions: [
            {
              id: 'pret_signature',
              type: 'choice',
              prompt: 'Prêt à signer ?',
              options: [
                { id: 'signer_oui', label: 'Oui, formalisons' },
                { id: 'signer_non', label: 'Pas encore' },
              ],
            },
          ],
        },
      ],
    },

    // ─── Étape 6 : Signature ────────────────────────────────────────
    {
      id: 'signature',
      title: 'Signature',
      clientTitle: 'Dernière étape',
      description: 'Nous formalisons l’accord.',
      problematisation: 'Souhaitez-vous vous engager maintenant ?',
      narrativeOverride: 'Le stylo est sur la table',
      narrativeArc: {
        position: 'resolution', tensionLevel: 2,
        previousStepLink: 'Un accord a été trouvé.',
      },
      stepSuccessCriteria: {
        minActionsRequired: 1,
        keyActionId: 'signer_accord',
        feedbackIfMissed: 'Sans signature, la probabilité de concrétisation chute.',
      },
      debriefMessage: 'Accord signé.',
      actions: [
        {
          id: 'proposer_lettre_intention',
          label: 'Proposer une lettre d’intention',
          clientLabel: 'Je signe la lettre d’intention',
          weight: 2,
          description: '« Bloquer la case et lancer les démarches. »',
          effect: { urgence_decision: 20, probabilite_accord: 15, profil_decision: 20 },
        },
        {
          id: 'signer_accord',
          label: 'Signer l’accord',
          clientLabel: 'Je signe l’accord définitif',
          weight: 3,
          description: 'Le document circule.',
          effect: { probabilite_accord: 50, valeur_percue: 20, conviction_directeur: 15, sante_projet: 30, profil_decision: 50 },
        },
      ],
      phases: [
        {
          type: 'observation',
          duration: 8,
          title: 'Le document',
          narrative: 'Présentation du contrat.',
          clientNarrative:
            'Voici le contrat reprenant les conditions que nous venons d’évoquer. Prenez le temps de le lire.',
          clientQuestion:
            'Puis-je vous laisser quelques instants pour le parcourir ?',
          sellerNote: 'Laisser le client lire tranquillement le contrat.',
          advanceLabel: 'Lire le contrat',
          clientActions: [
            {
              id: 'accord_lecture',
              type: 'choice',
              prompt: 'Puis-je vous laisser lire ?',
              options: [
                { id: 'lire_oui', label: 'Oui, je lis' },
                { id: 'lire_non', label: 'J\'ai une question avant' },
              ],
            },
          ],
        },
        {
          type: 'action',
          duration: 20,
          actionIds: ['proposer_lettre_intention', 'signer_accord'],
          clientActions: [
            {
              id: 'signature_client',
              type: 'choice',
              prompt: 'Quelle est votre décision ?',
              options: [
                { id: 'client_signe', label: 'Je signe l’accord', effect: { probabilite_accord: 30 } },
                { id: 'client_reflechi', label: 'Je préfère réfléchir encore', effect: { urgence_decision: -10 } },
              ],
            },
          ],
          narrative: 'Le client prend sa décision.',
          clientNarrative:
            'La balle est dans votre camp. Si vous avez la moindre question, je suis là.',
          clientQuestion:
            'Souhaitez-vous signer maintenant ou avez-vous besoin d’un délai ?',
          sellerNote: 'En cas d’hésitation, un rappel discret de l’audience estimée peut être utile.',
          advanceLabel: 'Finaliser',
        },
        {
          type: 'consequences',
          duration: 15,
          title: 'Après la signature',
          narrative: 'Accord officialisé.',
          clientNarrative:
            'Félicitations et merci de votre confiance. Nous allons pouvoir lancer la production.',
          clientQuestion:
            'Comment souhaitez-vous communiquer sur cette acquisition en interne ?',
          revealDelta: true,
          advanceLabel: 'Voir le résumé',
          clientActions: [
            {
              id: 'communication_interne',
              type: 'input',
              prompt: 'Votre suggestion de communication :',
              fieldLabel: 'Ex: un mail à l’équipe...',
            },
          ],
        },
      ],
    },
  ],
  simulationEngine: {
    dynamics: [
      { parameter: 'interet_directeur', formula: '0.85 * interet_directeur + 0.15 * conviction_directeur + 3 * (bande_annonce_vue ? 1 : 0)', fallback: 20, clampMin: 0, clampMax: 100, type: 'nonlinear', periodicity: 1, reportLabel: 'Intérêt du directeur' },
      { parameter: 'conviction_directeur', formula: '0.9 * conviction_directeur + 0.1 * (interet_directeur * 0.5 + etude_vue * 15)', fallback: 10, clampMin: 0, clampMax: 100, type: 'logistic', periodicity: 2, reportLabel: 'Conviction éditoriale' },
      { parameter: 'securite_financiere', formula: '0.8 * securite_financiere + 0.2 * roi_percu + 5 * (budget_vu ? 1 : 0)', fallback: 15, clampMin: 0, clampMax: 100, type: 'linear', periodicity: 2, reportLabel: 'Sécurité financière' },
      { parameter: 'tension_salle', formula: 'Math.max(0, tension_salle - 3) + 8 * (interet_directeur > 60 && securite_financiere < 40 ? 1 : 0)', fallback: 0, clampMin: 0, clampMax: 100, type: 'linear', periodicity: 1, reportLabel: 'Tension en salle' },
      { parameter: 'alignement_interne', formula: '0.5 * (conviction_directeur + securite_financiere) * 0.5 - 0.3 * tension_salle + 20 * (partage_risques_propose ? 1 : 0)', fallback: 20, clampMin: 0, clampMax: 100, type: 'nonlinear', periodicity: 2, reportLabel: 'Alignement interne' },
      { parameter: 'valeur_percue', formula: '0.4 * interet_directeur + 0.3 * securite_financiere + 0.2 * singularite + 0.1 * alignement_interne', fallback: 30, clampMin: 0, clampMax: 100, type: 'nonlinear', periodicity: 1, reportLabel: 'Valeur perçue' },
      { parameter: 'urgence_decision', formula: '0.7 * urgence_decision + 15 * (case_proposee ? 1 : 0) + 20 * (exclusivite_proposee ? 1 : 0)', fallback: 0, clampMin: 0, clampMax: 100, type: 'linear', periodicity: 1, reportLabel: 'Urgence' },
      { parameter: 'probabilite_accord', formula: '0.3 * conviction_directeur + 0.3 * securite_financiere + 0.2 * alignement_interne + 0.1 * valeur_percue + 0.1 * urgence_decision - 0.2 * tension_salle', fallback: 15, clampMin: 0, clampMax: 100, type: 'logistic', periodicity: 1, reportLabel: 'Probabilité d’accord' },
    ],
    computedMetrics: {
      bande_annonce_vue: "state.completedActions.includes('lancer_bande_annonce') ? 1 : 0",
      etude_vue: "state.completedActions.includes('montrer_etude_audience') ? 1 : 0",
      budget_vu: "state.completedActions.includes('presenter_budget_previsionnel') ? 1 : 0",
      case_proposee: "state.completedActions.includes('proposer_case_prime') ? 1 : 0",
      exclusivite_proposee: "state.completedActions.includes('accepter_exclusivite') ? 1 : 0",
      partage_risques_propose: "state.completedActions.includes('proposer_partage_risques') ? 1 : 0",
    },
    network: [
      { source: 'Direction des programmes', target: 'Contrôle financier', type: 'influence', strength: 0.6, evolving: true },
      { source: 'Contrôle financier', target: 'Direction des programmes', type: 'frein', strength: -0.4, evolving: true },
    ],
    quotes: [
      '« On parie sur une émotion collective. »',
      '« Le silence après une image forte est la preuve qu’elle a fonctionné. »',
      '« La signature, c’est le début – pas la fin. »',
    ],
    predictionModel: { enabled: true, method: 'bayesian', horizon: 6 },
    calibration: { enabled: false },
  },
  solutions: [
    {
      name: 'Accord signé — prime time confirmé',
      description: 'Documentaire acheté et programmé.',
      difficulty: 'expert',
      requiredActions: ['poser_question_editoriale', 'lancer_bande_annonce', 'garder_silence_apres_ba', 'presenter_angle_editorial', 'montrer_etude_audience', 'proposer_partage_risques', 'signer_accord'],
      expectedMetrics: { interet_directeur: 85, securite_financiere: 75, alignement_interne: 80, probabilite_accord: 90, profil_decision: 180 },
      interpretation: {
        shortSummary: 'Parcours complet : écoute, émotion, preuves, déblocage, signature.',
        keyInsight: 'L’écoute initiale a positionné le film comme une réponse.',
        theoreticalPromise: 95,
        realWorldProbability: 72,
        tone: 'radieux',
        competencies: [
          { name: 'Intelligence relationnelle', level: 'Expert', evidence: 'Question éditoriale, silence après projection.' },
          { name: 'Argumentation multi-niveaux', level: 'Expert', evidence: 'Émotion, conviction, preuves, déblocage.' },
          { name: 'Gestion de la friction', level: 'Expert', evidence: 'Partage de risques au bon moment.' },
        ],
      },
    },
    {
      name: 'Accord différé — lettre d’intention',
      description: 'Case non confirmée, lettre d’intention signée.',
      difficulty: 'avancé',
      requiredActions: ['lancer_bande_annonce', 'presenter_angle_editorial', 'montrer_etude_audience', 'proposer_lettre_intention'],
      expectedMetrics: { interet_directeur: 70, securite_financiere: 55, probabilite_accord: 60, profil_decision: 90 },
      interpretation: {
        shortSummary: 'Bon pitch, conclusion partielle.',
        keyInsight: 'Le partage de risques aurait peut-être permis de conclure.',
        theoreticalPromise: 75,
        realWorldProbability: 48,
        tone: 'lucide',
      },
    },
    {
      name: 'Intérêt sans engagement',
      description: 'Projet intéressant, rien de formalisé.',
      difficulty: 'intermédiaire',
      requiredActions: ['lancer_bande_annonce', 'montrer_etude_audience'],
      expectedMetrics: { interet_directeur: 55, securite_financiere: 30, probabilite_accord: 25, profil_decision: 50 },
      interpretation: {
        shortSummary: '« On vous revient » : dans 80 % des cas, un refus poli.',
        keyInsight: 'La friction interne n’a pas été résolue.',
        theoreticalPromise: 45,
        realWorldProbability: 22,
        tone: 'lucide',
      },
    },
  ],
  counterfactuals: [
    { actionId: 'poser_question_editoriale', actionTitle: 'Question éditoriale initiale', description: 'Sans elle, le film reste une proposition.', deltaProbability: 0.35 },
    { actionId: 'garder_silence_apres_ba', actionTitle: 'Silence après la projection', description: 'Le silence a permis une réaction authentique.', deltaProbability: 0.28 },
    { actionId: 'proposer_partage_risques', actionTitle: 'Partage de risques', description: 'C’est l’action qui a transformé la friction en accord.', deltaProbability: 0.42 },
    { actionId: 'presenter_angle_editorial', actionTitle: 'Logline commerciale', description: 'La phrase qui a voyagé.', deltaProbability: 0.30 },
  ],
  optimalPath: {
    actions: ['accepter_cafe', 'poser_question_editoriale', 'lancer_bande_annonce', 'montrer_interview_protagoniste', 'garder_silence_apres_ba', 'presenter_angle_editorial', 'raconter_origine_projet', 'montrer_etude_audience', 'presenter_plan_retombees', 'presenter_budget_previsionnel', 'proposer_case_prime', 'proposer_partage_risques', 'signer_accord'],
    predictedMetrics: { probabilite_accord: 92, interet_directeur: 88, securite_financiere: 78, alignement_interne: 85, profil_decision: 190 },
    probabilityOfSuccess: 0.92,
    generationFound: 1,
  },
  processMap: {
    nodes: [
      { id: 'start', label: 'Premier contact' },
      { id: 'emotion', label: 'Émotion créée' },
      { id: 'conviction', label: 'Conviction éditoriale' },
      { id: 'preuve', label: 'Preuves financières' },
      { id: 'friction', label: 'Objections gérées' },
      { id: 'accord', label: 'Accord formalisé' },
    ],
    edges: [
      { from: 'start', to: 'emotion' },
      { from: 'emotion', to: 'conviction' },
      { from: 'conviction', to: 'preuve' },
      { from: 'preuve', to: 'friction' },
      { from: 'friction', to: 'accord' },
    ],
  },
  optimizationGoals: { primary: 'maximiser probabilite_accord' },
};

// Template : 
const TPL_SERVICE2 = {
  slug: 'les-voix-du-fleuve-pitch',
  title: 'Les Voix du Fleuve — Pitch de diffusion',
  displayMode: 'vente',
  theme: 'dark',
  metadata: {
    versionEngine: '1.0 Pesacore',
    hasDynamicDebrief: true,
    estimatedDuration: '25 minutes',
    targetAudience: 'Directeur des programmes, Responsable des acquisitions',
    productionYear: 2025,
    genre: 'Documentaire environnemental / portrait humain',
    format: '90 minutes — diffusion prime time',
  },
  initialState: {
    parameters: {
      interet_directeur: 20, conviction_directeur: 10, confiance_directeur: 30,
      securite_financiere: 15, roi_percu: 10, confort_financier: 25,
      alignement_interne: 20, tension_salle: 0, valeur_percue: 30,
      urgence_decision: 0, singularite: 40, probabilite_accord: 15, profil_decision: 0,
    },
    agents: [
      {
        id: 'lambert', name: 'Philippe Lambert', narrativeName: 'Directeur des programmes', type: 'decisionnaire',
        characterTrait: 'Expérimenté, cherche une histoire forte.',
        reactionsTo: {
          poser_question_editoriale: { solo: 'Intérêt +20. Le dialogue est engagé.' },
          lancer_bande_annonce: { solo: 'Intérêt +25, conviction +10. Laisser le silence agir.' },
          montrer_interview_protagoniste: { solo: 'Singularité +20. Le visage de Maria renforce l’authenticité.' },
          presenter_angle_editorial: { solo: 'Conviction +20. Proposer une exclusivité.' },
          proposer_case_prime: { solo: 'Urgence +15. Case précise proposée.' },
          accepter_exclusivite: { solo: 'Urgence +20. Exclusivité en discussion.' },
          signer_accord: { solo: 'Probabilité finalisée. Contrat signé.' },
          marqué_histoire: { solo: 'Le client est touché par Maria. Conviction +15.' },
          marqué_images: { solo: 'Le client apprécie la qualité visuelle. Valeur perçue +10.' },
          reserve: { solo: 'Le client reste réservé. Tension +10.' },
          question_frein: { solo: 'Le client a une objection sur le budget. Continuer avec les chiffres.' },
          question_format: { solo: 'Le client s’interroge sur le format. Adapter le discours.' },
          question_disponibilite: { solo: 'Le client demande la disponibilité. Préparer une réponse précise.' },
          question_retombees: { solo: 'Le client veut des garanties sur les retombées. Montrer le plan de communication.' },
        },
      },
      {
        id: 'dubois', name: 'Marguerite Dubois', narrativeName: 'Responsable financière', type: 'financier',
        characterTrait: 'Prudente, cherche la faille.',
        reactionsTo: {
          montrer_etude_audience: { solo: 'Sécurité +25. Données Médiamétrie.' },
          presenter_budget_previsionnel: { solo: 'Budget 420k€ présenté.' },
          proposer_partage_risques: { solo: 'Alignement +25. Clause de performance.' },
          question_frein: { solo: 'La financière attendait cette question. Sécurité +5.' },
          question_disponibilite: { solo: 'La financière consulte son agenda. Urgence +10.' },
          question_retombees: { solo: 'La financière prend des notes. ROI perçu +10.' },
        },
      },
    ],
    families: [
      { name: 'Direction des programmes', isActive: true, isToxic: false, narrativeRole: 'Décide du positionnement éditorial.', agents: ['lambert'] },
      { name: 'Contrôle financier', isActive: true, isToxic: true, narrativeRole: 'Contrôle le budget d’acquisition.', agents: ['dubois'] },
    ],
    interactions: [
      { source: 'Direction des programmes', target: 'Contrôle financier', type: 'influence', strength: 0.6, evolving: true },
      { source: 'Contrôle financier', target: 'Direction des programmes', type: 'frein', strength: -0.4, evolving: true },
    ],
    theory: {
      primaryConcept: 'Émotion puis chiffres.',
      secondaryConcept: 'Un diffuseur achète une promesse d’audience et un risque calculé.',
      author: 'Marché des documentaires, 2022–2025', year: 2025,
      quote: 'On parie sur une émotion collective.',
      explanation: 'Convaincre par l’histoire, rassurer par les données.',
      quotes: [
        { actionId: 'lancer_bande_annonce', text: 'Les images vraies ont plus de force que les images parfaites.', author: 'Raymond Depardon' },
        { actionId: 'montrer_interview_protagoniste', text: 'Il n’y a pas de documentaire sans un visage.', author: 'Frederick Wiseman' },
        { actionId: 'montrer_etude_audience', text: 'Ce qui ne se mesure pas ne se gère pas – mais ce qui se mesure seulement ne se vit pas.', author: 'Anonyme, industrie TV' },
        { actionId: 'presenter_angle_editorial', text: 'L’angle, c’est le point de vue.', author: 'Chris Marker' },
        { actionId: 'proposer_case_prime', text: 'Le prime time est une promesse faite au spectateur.', author: 'Directeur de programmes, Arte' },
        { actionId: 'accepter_exclusivite', text: 'L’exclusivité, c’est du risque transformé en avantage.', author: 'Économie des médias, 2024' },
        { actionId: 'signer_accord', text: 'La signature, c’est le début – pas la fin.', author: 'Producteur, Festival de Périgueux' },
        { actionId: 'marqué_histoire', text: 'Un visage, une histoire. C’est tout ce dont un documentaire a besoin.', author: 'Frederick Wiseman' },
        { actionId: 'marqué_images', text: 'La beauté visuelle est la première promesse tenue.', author: 'Directeur de la photographie' },
        { actionId: 'reserve', text: 'La réserve est une forme d’attention. Elle mérite le respect.', author: 'Négociateur' },
      ],
      source: {
        title: 'Le marché du documentaire en France — Rapport annuel',
        author: 'Procirep & Angoa', year: 2024,
        url: 'https://procirep.fr/rapport-documentaire',
      },
    },
  },
  steps: [
    // ─── Étape 1 : Premier contact ──────────────────────────────────────
    {
      id: 'premier_contact',
      title: 'Premier contact',
      clientTitle: 'Avant de commencer',
      description: 'Nous engageons la conversation.',
      problematisation: 'Que manque-t-il dans votre grille actuelle ?',
      narrativeOverride: 'Deux décideurs. Quinze minutes. Un projet.',
      narrativeArc: {
        position: 'setup', tensionLevel: 1,
        nextStepForeshadow: 'Vous allez découvrir notre projet.',
      },
      stepSuccessCriteria: {
        minActionsRequired: 1,
        keyActionId: 'poser_question_editoriale',
        feedbackIfMissed: 'Sans question, la présentation reste générique.',
      },
      debriefMessage: 'Premier contact établi.',
      actions: [
        {
          id: 'accepter_cafe',
          label: 'Prendre le temps de s’installer',
          clientLabel: 'Je prends le temps de m’installer',
          weight: 1,
          description: 'Un geste simple pour instaurer une atmosphère de travail.',
          effect: { confiance_directeur: 5, tension_salle: -5, profil_decision: 5 },
        },
        {
          id: 'poser_question_editoriale',
          label: 'Poser une question sur leur grille',
          clientLabel: 'Je réponds à la question éditoriale',
          weight: 3,
          description: '« Qu’est-ce qui manque dans votre grille en ce moment ? »',
          effect: { interet_directeur: 20, confiance_directeur: 15, tension_salle: -10, profil_decision: 15 },
        },
      ],
      phases: [
        // Phase 0 : Observation enrichie d'un champ libre
        {
          type: 'observation',
          duration: 20,
          title: 'Installation',
          narrative: 'Vous êtes en visioconférence avec Philippe Lambert (Directeur des programmes) et Marguerite Dubois (Responsable financière).',
          clientNarrative:
            'Merci d’avoir rejoint cette réunion. L’équipe de production vous présente « Les Voix du Fleuve ». Si vous êtes prêts, nous allons commencer.',
          clientQuestion:
            'Avant d’entrer dans le vif du sujet, souhaitez-vous que l’on aborde un point particulier dès maintenant ?',
          sellerNote:
            'Ne commencez pas tout de suite. Laissez le client s’exprimer – sa réponse vous éclairera sur ses priorités.',
          advanceLabel: 'Répondre au producteur',
          // Ajout d'un mini input pour recueillir la réponse immédiate
          clientActions: [
            {
              id: 'attentes_initiales',
              type: 'input',
              prompt: 'Votre réponse :',
              fieldLabel: 'Un point particulier à aborder...',
            },
          ],
        },
        // Phase 1 : Action (dialogue principal)
        {
          type: 'action',
          duration: 30,
          title: 'Premier geste',
          actionIds: ['accepter_cafe', 'poser_question_editoriale'],
          clientActions: [
            {
              id: 'reponse_attentes',
              type: 'input',
              prompt: 'Vous répondez au producteur :',
              fieldLabel: 'Vos attentes ou besoins éditoriaux...',
            },
          ],
          narrative: 'Dialogue avec le directeur des programmes.',
          clientNarrative:
            'Je vous propose un café (virtuel) et nous engageons la discussion. Ma question est simple : selon vous, qu’est-ce qui manque le plus dans votre grille de programmes actuelle ?',
          clientQuestion:
            'Prenez le temps de la réflexion, je vous écoute.',
          sellerNote:
            'Notez la réponse du client. Elle vous servira de fil conducteur pour la suite.',
          advanceLabel: 'Poursuivre',
        },
        // Phase 2 : Conséquences enrichie d'un champ libre
        {
          type: 'consequences',
          duration: 15,
          title: 'Premières réactions',
          narrative: 'Retour du client sur ses attentes.',
          clientNarrative:
            'Très bien, votre retour est précieux. Nous allons maintenant entrer dans le vif du sujet.',
          clientQuestion:
            'Êtes-vous prêt à découvrir le projet ?',
          revealDelta: true,
          advanceLabel: "L'interlocuteur' est prêt",
          clientActions: [
            {
              id: 'confirmation_pret',
              type: 'choice',
              prompt: 'Confirmez-vous ?',
              options: [
                { id: 'pret_oui', label: 'Oui, je suis curieux' },
                { id: 'pret_non', label: 'Pas encore, une précision...' },
              ],
            },
          ],
        },
      ],
    },

    // ─── Étape 2 : Bande-annonce ──────────────────────────────────────
    {
      id: 'bande_annonce',
      title: 'Bande-annonce',
      clientTitle: 'Deux minutes. Pas une de plus.',
      description: 'Nous lançons la bande-annonce.',
      problematisation: 'Est-ce que ces deux minutes créent un impact durable ?',
      narrativeOverride: 'Silence en salle',
      narrativeArc: {
        position: 'setup', tensionLevel: 2,
        previousStepLink: 'La salle est attentive.',
        nextStepForeshadow: 'Vous allez réagir.',
      },
      stepSuccessCriteria: {
        minActionsRequired: 2,
        keyActionId: 'garder_silence_apres_ba',
        feedbackIfMissed: 'Vous avez parlé trop tôt. L’émotion n’a pas eu le temps de s’installer.',
      },
      debriefMessage: 'Projection terminée.',
      actions: [
        {
          id: 'lancer_bande_annonce',
          label: 'Lancer la bande-annonce',
          clientLabel: 'Je regarde la bande-annonce',
          weight: 3,
          description: 'Deux minutes deux secondes d’images.',
          effect: { interet_directeur: 25, conviction_directeur: 10, valeur_percue: 20, profil_decision: 20 },
          media: {
            type: 'youtube',
            url: 'https://www.youtube.com/watch?v=DYgf4YU2RJc',
            title: 'Bande-annonce officielle',
          },
        },
        {
          id: 'montrer_interview_protagoniste',
          label: 'Montrer l’interview de la protagoniste',
          clientLabel: 'Je découvre la protagoniste',
          weight: 2,
          description: 'Deux minutes d’un entretien brut avec Maria.',
          effect: { interet_directeur: 15, singularite: 20, valeur_percue: 10, profil_decision: 15 },
          media: {
            type: 'youtube',
            url: 'https://www.youtube.com/watch?v=autre_extrait', // À remplacer
            title: 'Interview de Maria',
          },
        },
        {
          id: 'garder_silence_apres_ba',
          label: 'Garder le silence après la projection',
          clientLabel: 'Je prends le temps d’absorber',
          weight: 3,
          description: 'Vous éteignez l’écran et attendez.',
          prerequisites: ['lancer_bande_annonce'],
          effect: { conviction_directeur: 15, confiance_directeur: 10, profil_decision: 15 },
        },
      ],
      phases: [
        // Observation : une question simple avec un input
        {
          type: 'observation',
          duration: 8,
          title: 'Préparation',
          narrative: 'Annonce de la projection au client.',
          clientNarrative:
            'Je vais vous montrer un extrait de deux minutes. Installez-vous confortablement.',
          clientQuestion: 'Prêt ?',
          sellerNote: 'Lancez la vidéo dès que le client est prêt.',
          advanceLabel: 'Lancer la vidéo',
          clientActions: [
            {
              id: 'confirmation_pret_ba',
              type: 'choice',
              prompt: 'Êtes-vous prêt à visionner ?',
              options: [
                { id: 'pret_ba_oui', label: 'Oui, je suis prêt' },
                { id: 'pret_ba_non', label: 'Attendez un instant...' },
              ],
            },
          ],
        },
        // Action (inchangée, elle contient déjà les clientActions)
        {
          type: 'action',
          duration: 45,
          title: 'Projection',
          actionIds: ['lancer_bande_annonce', 'montrer_interview_protagoniste', 'garder_silence_apres_ba'],
          clientActions: [
            {
              id: 'reaction_ba',
              type: 'choice',
              prompt: 'Qu’est-ce qui vous a le plus marqué ?',
              options: [
                { id: 'marqué_histoire', label: 'L’histoire de Maria', effect: { conviction_directeur: 15 } },
                { id: 'marqué_images',   label: 'La qualité des images', effect: { valeur_percue: 10 } },
                { id: 'reserve',         label: 'Je reste réservé',       effect: { tension_salle: 10 } },
              ],
            },
            {
              id: 'commentaire_ba',
              type: 'input',
              prompt: 'Un commentaire avant de continuer ?',
              fieldLabel: 'Votre commentaire...',
            },
          ],
          narrative: 'Diffusion de l’extrait choisi.',
          clientNarrative:
            'Voici l’extrait. Prenez le temps de le regarder, puis dites‑moi ce qui vous a touché.',
          clientQuestion:
            'Qu’est-ce qui vous a le plus marqué dans ce début ?',
          sellerNote:
            'N’interrompez pas la vidéo. Le silence fait partie de l’impact.',
          advanceLabel: 'Projection terminée',
        },
        // Conséquences avec input libre
        {
          type: 'consequences',
          duration: 20,
          title: 'Votre ressenti',
          narrative: 'Retour du client après la projection.',
          clientNarrative:
            'Merci pour votre retour. Nous allons maintenant approfondir pourquoi ce documentaire mérite une place sur votre antenne.',
          clientQuestion:
            'Souhaitez-vous que je développe un point en particulier avant d’aller plus loin ?',
          revealDelta: true,
          advanceLabel: 'Continuer',
          clientActions: [
            {
              id: 'precision_supplementaire',
              type: 'input',
              prompt: 'Un point à développer ?',
              fieldLabel: 'Votre demande...',
            },
          ],
        },
      ],
    },

    // ─── Étape 3 : Argument éditorial ───────────────────────────────
    {
      id: 'argument_editorial',
      title: 'Argument éditorial',
      clientTitle: 'Pourquoi ce film, pourquoi maintenant ?',
      description: 'Nous présentons l’angle du documentaire.',
      problematisation: 'Qu’est-ce qui rend ce projet unique pour votre chaîne ?',
      narrativeOverride: 'L’émotion ne suffit pas',
      narrativeArc: {
        position: 'escalade', tensionLevel: 3,
        previousStepLink: 'L’émotion a créé l’ouverture.',
        nextStepForeshadow: 'Des questions vont émerger.',
      },
      stepSuccessCriteria: {
        minActionsRequired: 1,
        keyActionId: 'presenter_angle_editorial',
        feedbackIfMissed: 'Sans angle fort, le projet risque d’être difficile à défendre en interne.',
      },
      debriefMessage: 'Argument éditorial présenté.',
      actions: [
        {
          id: 'presenter_angle_editorial',
          label: 'Présenter l’angle en une phrase',
          clientLabel: 'Je retiens l’angle éditorial',
          weight: 3,
          exclusiveGroup: 'argument_cle',
          description: '« Les Voix du Fleuve, c’est l’histoire d’une femme qui se bat pour un fleuve que personne d’autre ne défend. »',
          effect: { conviction_directeur: 20, interet_directeur: 15, singularite: 15, profil_decision: 25 },
        },
        {
          id: 'montrer_comparatif_marche',
          label: 'Montrer le comparatif de marché',
          clientLabel: 'Je consulte le contexte marché',
          weight: 2,
          exclusiveGroup: 'argument_cle',
          description: 'Trois documentaires similaires récents et leur impact.',
          effect: { securite_financiere: 15, roi_percu: 10, profil_decision: 15 },
        },
        {
          id: 'raconter_origine_projet',
          label: 'Raconter l’origine du projet',
          clientLabel: 'Je découvre l’histoire derrière le film',
          weight: 2,
          exclusiveGroup: 'argument_cle',
          description: '« Un réalisateur est resté huit mois sur place. »',
          effect: { confiance_directeur: 20, singularite: 10, profil_decision: 15 },
        },
      ],
      phases: [
        // Observation avec input
        {
          type: 'observation',
          duration: 10,
          title: 'Question du directeur',
          narrative: 'Le directeur des programmes interroge sur la pertinence du projet.',
          clientNarrative:
            'Je comprends que vous ayez besoin de savoir pourquoi ce film, et pourquoi votre chaîne. Laissez-moi vous donner la phrase clé.',
          clientQuestion:
            'Pour vous, qu’est-ce qui fait qu’un documentaire devient incontournable dans une grille ?',
          sellerNote:
            'Votre réponse doit lier le moment, la chaîne et le public cible.',
          advanceLabel: 'Écouter l’argument',
          clientActions: [
            {
              id: 'definition_incontournable',
              type: 'input',
              prompt: 'D’après vous, un documentaire est incontournable quand...',
              fieldLabel: 'Votre critère principal...',
            },
          ],
        },
        // Action (choix exclusif) inchangée
        {
          type: 'action',
          duration: 35,
          actionIds: ['presenter_angle_editorial', 'montrer_comparatif_marche', 'raconter_origine_projet'],
          clientActions: [
            {
              id: 'argument_choix_client',
              type: 'choice',
              prompt: 'Quel argument vous semble le plus solide ?',
              options: [
                { id: 'client_angle_editorial', label: 'L\'angle éditorial (l\'histoire de Maria)', effect: { conviction_directeur: 10 } },
                { id: 'client_comparatif', label: 'Le comparatif de marché', effect: { securite_financiere: 10 } },
                { id: 'client_origine', label: 'L\'origine du projet', effect: { singularite: 10 } },
              ],
            },
          ],
          narrative: 'Le vendeur présente les arguments.',
          clientNarrative:
            'Voici trois façons de présenter le projet. Je vous laisse me dire ce qui vous parle le plus.',
          clientQuestion:
            'À votre avis, quel est l’argument le plus percutant pour votre comité de programmes ?',
          sellerNote:
            'Une idée forte vaut mieux que trois idées moyennes.',
          advanceLabel: 'Valider le choix',
        },
        // Conséquences avec input
        {
          type: 'consequences',
          duration: 15,
          title: 'Effet de l’argument',
          narrative: 'Le client a choisi son angle préféré.',
          clientNarrative:
            'Parfait, nous allons donc approfondir cet axe. Passons maintenant à des éléments plus concrets pour rassurer votre direction financière.',
          clientQuestion:
            'Cela vous convient-il comme base de discussion ?',
          revealDelta: true,
          advanceLabel: 'Passons aux chiffres',
          clientActions: [
            {
              id: 'confirmation_base',
              type: 'choice',
              prompt: 'Cela vous convient ?',
              options: [
                { id: 'convient_oui', label: 'Oui, allons-y' },
                { id: 'convient_non', label: 'J\'aimerais une précision' },
              ],
            },
          ],
        },
      ],
    },

    // ─── Étape 4 : Épreuve des chiffres ─────────────────────────────
    {
      id: 'epreuve_chiffres',
      title: 'Épreuve des chiffres',
      clientTitle: 'Les chiffres ont la parole',
      description: 'Nous présentons les données d’audience et le budget.',
      problematisation: 'Comment évaluer la viabilité financière de ce projet ?',
      narrativeOverride: 'Des questions précises',
      narrativeArc: {
        position: 'pivot', tensionLevel: 4,
        previousStepLink: 'Le directeur est convaincu.',
        nextStepForeshadow: 'Si les chiffres tiennent, l’accord est possible.',
      },
      stepSuccessCriteria: {
        minActionsRequired: 2,
        keyActionId: 'montrer_etude_audience',
        feedbackIfMissed: 'Sans étude, la partie financière peut bloquer.',
      },
      debriefMessage: 'Données présentées.',
      actions: [
        {
          id: 'montrer_etude_audience',
          label: 'Présenter l’étude d’audience',
          clientLabel: 'Je consulte les projections Médiamétrie',
          weight: 3,
          description: '2,3 millions de téléspectateurs potentiels.',
          effect: { securite_financiere: 25, roi_percu: 20, alignement_interne: 10, profil_decision: 25 },
          media: {
            type: 'image',
            url: 'https://via.placeholder.com/900x500?text=Etude+Mediametrie',
            title: 'Projections d’audience Médiamétrie',
          },
        },
        {
          id: 'presenter_plan_retombees',
          label: 'Présenter le plan de retombées',
          clientLabel: 'Je découvre le plan de communication',
          weight: 2,
          description: 'Partenariat presse, festival, campagne digitale.',
          effect: { roi_percu: 15, confort_financier: 20, profil_decision: 15 },
        },
        {
          id: 'presenter_budget_previsionnel',
          label: 'Présenter le budget',
          clientLabel: 'Je prends connaissance du budget',
          weight: 2,
          description: 'Budget total : 420 k€. Demande : 180 k€.',
          effect: { securite_financiere: 20, roi_percu: 10, profil_decision: 20 },
          media: {
            type: 'image',
            url: 'https://via.placeholder.com/900x500?text=Budget+Previsionnel',
            title: 'Budget prévisionnel',
          },
        },
      ],
      phases: [
        // Observation avec input
        {
          type: 'observation',
          duration: 8,
          title: 'Questions financières',
          narrative: 'La responsable financière prend la parole.',
          clientNarrative:
            'Nous entrons dans la partie chiffrée. Voici les projections d’audience réalisées avec Médiamétrie, ainsi que le budget détaillé.',
          clientQuestion:
            'Y a-t-il un élément financier qui vous préoccupe particulièrement avant que je commence ?',
          sellerNote:
            'Restez factuel. Si une question vous dépasse, proposez d’envoyer l’information plus tard.',
          advanceLabel: 'Présenter les données',
          clientActions: [
            {
              id: 'preoccupation_financiere',
              type: 'input',
              prompt: 'Une inquiétude particulière ?',
              fieldLabel: 'Élément financier...',
            },
          ],
        },
        // Action inchangée
        {
          type: 'action',
          duration: 40,
          actionIds: ['montrer_etude_audience', 'presenter_plan_retombees', 'presenter_budget_previsionnel'],
          clientActions: [
            {
              id: 'objections_chiffres',
              type: 'choice',
              prompt: 'Quel point souhaitez-vous que je détaille ?',
              options: [
                { id: 'question_frein', label: 'Le budget et sa négociabilité', effect: { securite_financiere: 5, tension_salle: 5 } },
                { id: 'question_format', label: 'L’adaptation du format 90 min', effect: { interet_directeur: 5 } },
                { id: 'question_retombees', label: 'Les garanties de retombées presse', effect: { roi_percu: 10 } },
              ],
            },
            {
              id: 'question_complementaire',
              type: 'input',
              prompt: 'Une autre question sur ces chiffres ?',
              fieldLabel: 'Votre question...',
            },
          ],
          narrative: 'Analyse des chiffres par le client.',
          clientNarrative:
            'Prenez le temps d’examiner ces graphiques. Je reste à votre disposition pour toute clarification.',
          clientQuestion:
            'Parmi ces trois aspects, lequel voulez-vous que je développe en priorité ?',
          sellerNote:
            'Ne lisez pas les slides. Commentez et contextualisez.',
          advanceLabel: 'Les chiffres sont clairs',
        },
        // Conséquences avec input
        {
          type: 'consequences',
          duration: 15,
          title: 'Arbitrage',
          narrative: 'Discussion sur la viabilité financière.',
          clientNarrative:
            'Merci. Avec ces éléments, je pense que vous avez une vision claire du potentiel de retour sur investissement.',
          clientQuestion:
            'Ces chiffres vous semblent-ils solides pour avancer ?',
          revealDelta: true,
          advanceLabel: 'Aller vers la conclusion',
          clientActions: [
            {
              id: 'confirmation_chiffres',
              type: 'choice',
              prompt: 'Ces chiffres vous rassurent-ils ?',
              options: [
                { id: 'rassure_oui', label: 'Oui, continuons' },
                { id: 'rassure_non', label: 'Je souhaite un complément' },
              ],
            },
          ],
        },
      ],
    },

    // ─── Étape 5 : Négociation et objections ────────────────────────
    {
      id: 'negociation_objections',
      title: 'Négociation et objections',
      clientTitle: 'Discutons des conditions',
      description: 'Nous répondons aux objections et cherchons un accord.',
      problematisation: 'Quelle solution répond le mieux à vos attentes ?',
      narrativeOverride: 'Le moment de décider',
      narrativeArc: {
        position: 'climax', tensionLevel: 5,
        previousStepLink: 'Les deux parties ont les mêmes données.',
        nextStepForeshadow: 'Si un terrain d’entente est trouvé, la signature est proche.',
      },
      stepSuccessCriteria: {
        minActionsRequired: 1,
        keyActionId: 'proposer_partage_risques',
        feedbackIfMissed: 'Sans solution, la réunion peut se terminer sans engagement.',
      },
      debriefMessage: 'Solution proposée.',
      actions: [
        {
          id: 'proposer_case_prime',
          label: 'Proposer une case précise',
          clientLabel: 'Je prends note de la case proposée',
          weight: 2,
          exclusiveGroup: 'solution_objection',
          description: '« Dimanche soir à 20 h 50 en janvier. »',
          effect: { conviction_directeur: 15, urgence_decision: 15, profil_decision: 20 },
        },
        {
          id: 'accepter_exclusivite',
          label: 'Proposer une exclusivité',
          clientLabel: 'J’étudie l’exclusivité',
          weight: 2,
          exclusiveGroup: 'solution_objection',
          description: '« Exclusivité sur 24 mois si signature avant le 15. »',
          effect: { interet_directeur: 20, urgence_decision: 20, profil_decision: 20 },
        },
        {
          id: 'proposer_partage_risques',
          label: 'Proposer un partage de risques',
          clientLabel: 'J’envisage le partage de risques',
          weight: 3,
          exclusiveGroup: 'solution_objection',
          description: '« Si audience > 1,8 M, clause de rediffusion. Sinon, prise en charge de 30 %. »',
          effect: { securite_financiere: 30, alignement_interne: 25, tension_salle: -15, profil_decision: 30 },
        },
      ],
      phases: [
        // Observation avec input
        {
          type: 'observation',
          duration: 10,
          title: 'Objections possibles',
          narrative: 'Le client exprime des réserves.',
          clientNarrative:
            'Je sens qu’il y a peut-être encore quelques hésitations. N’hésitez pas à me les partager, c’est normal à ce stade.',
          clientQuestion:
            'Qu’est-ce qui, aujourd’hui, pourrait vous empêcher de donner votre accord ?',
          sellerNote:
            'Ne prenez pas parti. Écoutez les deux interlocuteurs.',
          advanceLabel: 'Proposer une solution',
          clientActions: [
            {
              id: 'frein_principal',
              type: 'input',
              prompt: 'Votre principal frein :',
              fieldLabel: 'Ce qui vous inquiète...',
            },
          ],
        },
        // Action inchangée
        {
          type: 'action',
          duration: 40,
          actionIds: ['proposer_case_prime', 'accepter_exclusivite', 'proposer_partage_risques'],
          clientActions: [
            {
              id: 'conditions_finales',
              type: 'choice',
              prompt: 'Laquelle de ces conditions vous semble la plus rassurante ?',
              options: [
                { id: 'question_disponibilite', label: 'Une date de diffusion garantie', effect: { urgence_decision: 10 } },
                { id: 'question_frein', label: 'Une flexibilité sur le budget', effect: { securite_financiere: 5, tension_salle: 5 } },
                { id: 'question_retombees', label: 'Un plan de communication détaillé', effect: { roi_percu: 10 } },
              ],
            },
          ],
          narrative: 'Négociation en cours.',
          clientNarrative:
            'Voici plusieurs options que nous pouvons mettre en place pour sécuriser l’opération. Je vous écoute.',
          clientQuestion:
            'Quelle approche vous correspond le mieux ?',
          sellerNote:
            'Gardez le partage de risques pour la fin si possible.',
          advanceLabel: 'Décider',
        },
        // Conséquences avec input
        {
          type: 'consequences',
          duration: 20,
          title: 'Vers un accord',
          narrative: 'Un terrain d’entente est trouvé.',
          clientNarrative:
            'Très bien, cette option est tout à fait envisageable pour nous. Nous pouvons maintenant formaliser.',
          clientQuestion:
            'Êtes-vous prêt à passer à l’étape de signature ?',
          revealDelta: true,
          advanceLabel: 'Formaliser',
          clientActions: [
            {
              id: 'pret_signature',
              type: 'choice',
              prompt: 'Prêt à signer ?',
              options: [
                { id: 'signer_oui', label: 'Oui, formalisons' },
                { id: 'signer_non', label: 'Pas encore' },
              ],
            },
          ],
        },
      ],
    },

    // ─── Étape 6 : Signature ────────────────────────────────────────
    {
      id: 'signature',
      title: 'Signature',
      clientTitle: 'Dernière étape',
      description: 'Nous formalisons l’accord.',
      problematisation: 'Souhaitez-vous vous engager maintenant ?',
      narrativeOverride: 'Le stylo est sur la table',
      narrativeArc: {
        position: 'resolution', tensionLevel: 2,
        previousStepLink: 'Un accord a été trouvé.',
      },
      stepSuccessCriteria: {
        minActionsRequired: 1,
        keyActionId: 'signer_accord',
        feedbackIfMissed: 'Sans signature, la probabilité de concrétisation chute.',
      },
      debriefMessage: 'Accord signé.',
      actions: [
        {
          id: 'proposer_lettre_intention',
          label: 'Proposer une lettre d’intention',
          clientLabel: 'Je signe la lettre d’intention',
          weight: 2,
          description: '« Bloquer la case et lancer les démarches. »',
          effect: { urgence_decision: 20, probabilite_accord: 15, profil_decision: 20 },
        },
        {
          id: 'signer_accord',
          label: 'Signer l’accord',
          clientLabel: 'Je signe l’accord définitif',
          weight: 3,
          description: 'Le document circule.',
          effect: { probabilite_accord: 50, valeur_percue: 20, conviction_directeur: 15, sante_projet: 30, profil_decision: 50 },
        },
      ],
      phases: [
        // Observation avec input
        {
          type: 'observation',
          duration: 8,
          title: 'Le document',
          narrative: 'Présentation du contrat.',
          clientNarrative:
            'Voici le contrat reprenant les conditions que nous venons d’évoquer. Prenez le temps de le lire.',
          clientQuestion:
            'Puis-je vous laisser quelques instants pour le parcourir ?',
          sellerNote:
            'Ne parlez pas pendant la lecture.',
          advanceLabel: 'Lire le contrat',
          clientActions: [
            {
              id: 'accord_lecture',
              type: 'choice',
              prompt: 'Puis-je vous laisser lire ?',
              options: [
                { id: 'lire_oui', label: 'Oui, je lis' },
                { id: 'lire_non', label: 'J\'ai une question avant' },
              ],
            },
          ],
        },
        // Action inchangée
        {
          type: 'action',
          duration: 20,
          actionIds: ['proposer_lettre_intention', 'signer_accord'],
          clientActions: [
            {
              id: 'signature_client',
              type: 'choice',
              prompt: 'Quelle est votre décision ?',
              options: [
                { id: 'client_signe', label: 'Je signe l’accord', effect: { probabilite_accord: 30 } },
                { id: 'client_reflechi', label: 'Je préfère réfléchir encore', effect: { urgence_decision: -10 } },
              ],
            },
          ],
          narrative: 'Le client prend sa décision.',
          clientNarrative:
            'La balle est dans votre camp. Si vous avez la moindre question, je suis là.',
          clientQuestion:
            'Souhaitez-vous signer maintenant ou avez-vous besoin d’un délai ?',
          sellerNote:
            'Si hésitation, rappelez discrètement l’audience estimée.',
          advanceLabel: 'Finaliser',
        },
        // Conséquences avec input
        {
          type: 'consequences',
          duration: 15,
          title: 'Après la signature',
          narrative: 'Accord officialisé.',
          clientNarrative:
            'Félicitations et merci de votre confiance. Nous allons pouvoir lancer la production.',
          clientQuestion:
            'Comment souhaitez-vous communiquer sur cette acquisition en interne ?',
          revealDelta: true,
          advanceLabel: 'Voir le résumé',
          clientActions: [
            {
              id: 'communication_interne',
              type: 'input',
              prompt: 'Votre suggestion de communication :',
              fieldLabel: 'Ex: un mail à l’équipe...',
            },
          ],
        },
      ],
    },
  ],
  simulationEngine: {
    dynamics: [
      { parameter: 'interet_directeur', formula: '0.85 * interet_directeur + 0.15 * conviction_directeur + 3 * (bande_annonce_vue ? 1 : 0)', fallback: 20, clampMin: 0, clampMax: 100, type: 'nonlinear', periodicity: 1, reportLabel: 'Intérêt du directeur' },
      { parameter: 'conviction_directeur', formula: '0.9 * conviction_directeur + 0.1 * (interet_directeur * 0.5 + etude_vue * 15)', fallback: 10, clampMin: 0, clampMax: 100, type: 'logistic', periodicity: 2, reportLabel: 'Conviction éditoriale' },
      { parameter: 'securite_financiere', formula: '0.8 * securite_financiere + 0.2 * roi_percu + 5 * (budget_vu ? 1 : 0)', fallback: 15, clampMin: 0, clampMax: 100, type: 'linear', periodicity: 2, reportLabel: 'Sécurité financière' },
      { parameter: 'tension_salle', formula: 'Math.max(0, tension_salle - 3) + 8 * (interet_directeur > 60 && securite_financiere < 40 ? 1 : 0)', fallback: 0, clampMin: 0, clampMax: 100, type: 'linear', periodicity: 1, reportLabel: 'Tension en salle' },
      { parameter: 'alignement_interne', formula: '0.5 * (conviction_directeur + securite_financiere) * 0.5 - 0.3 * tension_salle + 20 * (partage_risques_propose ? 1 : 0)', fallback: 20, clampMin: 0, clampMax: 100, type: 'nonlinear', periodicity: 2, reportLabel: 'Alignement interne' },
      { parameter: 'valeur_percue', formula: '0.4 * interet_directeur + 0.3 * securite_financiere + 0.2 * singularite + 0.1 * alignement_interne', fallback: 30, clampMin: 0, clampMax: 100, type: 'nonlinear', periodicity: 1, reportLabel: 'Valeur perçue' },
      { parameter: 'urgence_decision', formula: '0.7 * urgence_decision + 15 * (case_proposee ? 1 : 0) + 20 * (exclusivite_proposee ? 1 : 0)', fallback: 0, clampMin: 0, clampMax: 100, type: 'linear', periodicity: 1, reportLabel: 'Urgence' },
      { parameter: 'probabilite_accord', formula: '0.3 * conviction_directeur + 0.3 * securite_financiere + 0.2 * alignement_interne + 0.1 * valeur_percue + 0.1 * urgence_decision - 0.2 * tension_salle', fallback: 15, clampMin: 0, clampMax: 100, type: 'logistic', periodicity: 1, reportLabel: 'Probabilité d’accord' },
    ],
    computedMetrics: {
      bande_annonce_vue: "state.completedActions.includes('lancer_bande_annonce') ? 1 : 0",
      etude_vue: "state.completedActions.includes('montrer_etude_audience') ? 1 : 0",
      budget_vu: "state.completedActions.includes('presenter_budget_previsionnel') ? 1 : 0",
      case_proposee: "state.completedActions.includes('proposer_case_prime') ? 1 : 0",
      exclusivite_proposee: "state.completedActions.includes('accepter_exclusivite') ? 1 : 0",
      partage_risques_propose: "state.completedActions.includes('proposer_partage_risques') ? 1 : 0",
    },
    network: [
      { source: 'Direction des programmes', target: 'Contrôle financier', type: 'influence', strength: 0.6, evolving: true },
      { source: 'Contrôle financier', target: 'Direction des programmes', type: 'frein', strength: -0.4, evolving: true },
    ],
    quotes: [
      '« On parie sur une émotion collective. »',
      '« Le silence après une image forte est la preuve qu’elle a fonctionné. »',
      '« La signature, c’est le début – pas la fin. »',
    ],
    predictionModel: { enabled: true, method: 'bayesian', horizon: 6 },
    calibration: { enabled: false },
  },
  solutions: [
    {
      name: 'Accord signé — prime time confirmé',
      description: 'Documentaire acheté et programmé.',
      difficulty: 'expert',
      requiredActions: ['poser_question_editoriale', 'lancer_bande_annonce', 'garder_silence_apres_ba', 'presenter_angle_editorial', 'montrer_etude_audience', 'proposer_partage_risques', 'signer_accord'],
      expectedMetrics: { interet_directeur: 85, securite_financiere: 75, alignement_interne: 80, probabilite_accord: 90, profil_decision: 180 },
      interpretation: {
        shortSummary: 'Parcours complet : écoute, émotion, preuves, déblocage, signature.',
        keyInsight: 'L’écoute initiale a positionné le film comme une réponse.',
        theoreticalPromise: 95,
        realWorldProbability: 72,
        tone: 'radieux',
        competencies: [
          { name: 'Intelligence relationnelle', level: 'Expert', evidence: 'Question éditoriale, silence après projection.' },
          { name: 'Argumentation multi-niveaux', level: 'Expert', evidence: 'Émotion, conviction, preuves, déblocage.' },
          { name: 'Gestion de la friction', level: 'Expert', evidence: 'Partage de risques au bon moment.' },
        ],
      },
    },
    {
      name: 'Accord différé — lettre d’intention',
      description: 'Case non confirmée, lettre d’intention signée.',
      difficulty: 'avancé',
      requiredActions: ['lancer_bande_annonce', 'presenter_angle_editorial', 'montrer_etude_audience', 'proposer_lettre_intention'],
      expectedMetrics: { interet_directeur: 70, securite_financiere: 55, probabilite_accord: 60, profil_decision: 90 },
      interpretation: {
        shortSummary: 'Bon pitch, conclusion partielle.',
        keyInsight: 'Le partage de risques aurait peut-être permis de conclure.',
        theoreticalPromise: 75,
        realWorldProbability: 48,
        tone: 'lucide',
      },
    },
    {
      name: 'Intérêt sans engagement',
      description: 'Projet intéressant, rien de formalisé.',
      difficulty: 'intermédiaire',
      requiredActions: ['lancer_bande_annonce', 'montrer_etude_audience'],
      expectedMetrics: { interet_directeur: 55, securite_financiere: 30, probabilite_accord: 25, profil_decision: 50 },
      interpretation: {
        shortSummary: '« On vous revient » : dans 80 % des cas, un refus poli.',
        keyInsight: 'La friction interne n’a pas été résolue.',
        theoreticalPromise: 45,
        realWorldProbability: 22,
        tone: 'lucide',
      },
    },
  ],
  counterfactuals: [
    { actionId: 'poser_question_editoriale', actionTitle: 'Question éditoriale initiale', description: 'Sans elle, le film reste une proposition.', deltaProbability: 0.35 },
    { actionId: 'garder_silence_apres_ba', actionTitle: 'Silence après la projection', description: 'Le silence a permis une réaction authentique.', deltaProbability: 0.28 },
    { actionId: 'proposer_partage_risques', actionTitle: 'Partage de risques', description: 'C’est l’action qui a transformé la friction en accord.', deltaProbability: 0.42 },
    { actionId: 'presenter_angle_editorial', actionTitle: 'Logline commerciale', description: 'La phrase qui a voyagé.', deltaProbability: 0.30 },
  ],
  optimalPath: {
    actions: ['accepter_cafe', 'poser_question_editoriale', 'lancer_bande_annonce', 'montrer_interview_protagoniste', 'garder_silence_apres_ba', 'presenter_angle_editorial', 'raconter_origine_projet', 'montrer_etude_audience', 'presenter_plan_retombees', 'presenter_budget_previsionnel', 'proposer_case_prime', 'proposer_partage_risques', 'signer_accord'],
    predictedMetrics: { probabilite_accord: 92, interet_directeur: 88, securite_financiere: 78, alignement_interne: 85, profil_decision: 190 },
    probabilityOfSuccess: 0.92,
    generationFound: 1,
  },
  processMap: {
    nodes: [
      { id: 'start', label: 'Premier contact' },
      { id: 'emotion', label: 'Émotion créée' },
      { id: 'conviction', label: 'Conviction éditoriale' },
      { id: 'preuve', label: 'Preuves financières' },
      { id: 'friction', label: 'Objections gérées' },
      { id: 'accord', label: 'Accord formalisé' },
    ],
    edges: [
      { from: 'start', to: 'emotion' },
      { from: 'emotion', to: 'conviction' },
      { from: 'conviction', to: 'preuve' },
      { from: 'preuve', to: 'friction' },
      { from: 'friction', to: 'accord' },
    ],
  },
  optimizationGoals: { primary: 'maximiser probabilite_accord' },
};

const TPL_GENERIC_SALES = {
  slug: 'vente-b2b-generique',
  title: 'Vente B2B – Parcours décisionnel complet',
  pedagogicalGoal: null, // nous sommes en mode vente pure, pas de formation
  displayMode: 'vente',
  theme: 'dark',

  metadata: {
    versionEngine: '1.0 Pesacore',
    hasDynamicDebrief: true,
    estimatedDuration: '30 minutes',
    targetAudience: 'Directeur général, Directeur financier, Responsable achats',
    productionYear: 2025,
    genre: 'Simulation de vente B2B',
    format: 'Réunion décisionnelle – 45 min',
  },

  // ── État initial ────────────────────────────────────────────────────────────
  initialState: {
    parameters: {
      interet_decisionnaire: 25,
      confiance_decisionnaire: 15,
      budget_percu: 20,
      alignement_strategique: 20,
      urgence_achat: 5,
      valeur_percue: 25,
      friction_interne: 15,
      probabilite_accord: 10,
      profil_global: 0,
    },

    agents: [
      {
        id: 'dg',
        name: 'Marc Venier',
        narrativeName: 'Directeur Général',
        type: 'decisionnaire',
        characterTrait: 'Visionnaire, centré sur la croissance.',
        reactionsTo: {
          question_strategique: { solo: 'Intérêt +25. Il se sent écouté.' },
          demo_produit: { solo: 'Valeur perçue +20, confiance +10.' },
          partage_etude_cas: { solo: 'Alignement stratégique +15.' },
          proposition_roi: { solo: 'Budget perçu +20, urgence +10.' },
          repondre_objection_technique: { solo: 'Confiance +15.' },
          proposer_phasage: { solo: 'Urgence +15, friction interne -10.' },
          signer_contrat: { solo: 'Probabilité finalisée.' },
        },
      },
      {
        id: 'daf',
        name: 'Sophie Morel',
        narrativeName: 'Directrice Administrative & Financière',
        type: 'financier',
        characterTrait: 'Prudente, focalisée sur le retour sur investissement.',
        reactionsTo: {
          montrer_roi_detail: { solo: 'Budget perçu +25, friction -10.' },
          partager_references: { solo: 'Confiance +15.' },
          repondre_objection_prix: { solo: 'Friction -20 si réponse claire.' },
          proposer_clause_securite: { solo: 'Alignement +20, friction -15.' },
        },
      },
    ],

    families: [
      {
        name: 'Direction Générale',
        isActive: true,
        isToxic: false,
        narrativeRole: 'Valide la vision et l’impact stratégique.',
        agents: ['dg'],
      },
      {
        name: 'Contrôle Financier',
        isActive: true,
        isToxic: true,
        narrativeRole: 'Valide la viabilité économique et limite les risques.',
        agents: ['daf'],
      },
    ],

    interactions: [
      {
        source: 'Direction Générale',
        target: 'Contrôle Financier',
        type: 'influence',
        strength: 0.7,
        evolving: true,
      },
      {
        source: 'Contrôle Financier',
        target: 'Direction Générale',
        type: 'frein',
        strength: -0.5,
        evolving: true,
      },
    ],

    theory: {
      primaryConcept: 'Créer de la valeur puis rassurer sur le risque.',
      secondaryConcept:
        'Un décideur achète un avenir meilleur, un financier achète un risque maîtrisé.',
      author: 'Méthodologie Pesacore – vente complexe',
      year: 2025,
      quote: 'On n’achète pas un produit, on achète un résultat.',
      explanation:
        'Phase 1 : alignement stratégique. Phase 2 : démonstration de la valeur. Phase 3 : gestion des risques. Phase 4 : engagement.',
      quotes: [
        { actionId: 'question_strategique', text: 'Comprendre avant de convaincre.', author: 'Pesacore' },
        { actionId: 'demo_produit', text: 'Ce qui se voit se croit.', author: 'Sagesse commerciale' },
        { actionId: 'proposition_roi', text: 'Un chiffre vaut mieux qu’un long discours.', author: 'Warren Buffet' },
      ],
      source: {
        title: 'Guide de la vente complexe B2B',
        author: 'Pesacore Research',
        year: 2025,
        url: 'https://pesacore.io/methodology',
      },
    },
  },

  // ── Étapes ───────────────────────────────────────────────────────────────────
  steps: [
    // Étape 1 : Connexion stratégique
    {
      id: 'connexion_strategique',
      title: 'Connexion stratégique',
      clientTitle: 'Pourquoi sommes-nous ici ?',
      description: 'Nous créons un lien et alignons nos visions.',
      problematisation: 'Quels sont vos enjeux actuels ?',
      narrativeOverride: 'Le DG ouvre la réunion.',
      narrativeArc: {
        position: 'setup',
        tensionLevel: 1,
        nextStepForeshadow: 'Vous allez découvrir notre proposition.',
      },
      learningObjectives: [],
      stepSuccessCriteria: {
        minActionsRequired: 1,
        keyActionId: 'question_strategique',
        feedbackIfMissed: 'Sans question, pas d’alignement.',
      },
      debriefMessage: 'Connexion établie.',
      actions: [
        {
          id: 'accueil',
          label: 'Accueillir et remercier',
          clientLabel: 'Je prends place',
          weight: 1,
          description: 'Un accueil professionnel qui pose le cadre.',
          effect: { confiance_decisionnaire: 5, friction_interne: -5, profil_global: 5 },
        },
        {
          id: 'question_strategique',
          label: 'Poser une question stratégique',
          clientLabel: 'Je réponds sur nos enjeux',
          weight: 3,
          description: '« Qu’est-ce qui, selon vous, pourrait accélérer votre croissance cette année ? »',
          effect: { interet_decisionnaire: 25, confiance_decisionnaire: 15, friction_interne: -10, profil_global: 20 },
        },
      ],
      phases: [
        {
          type: 'observation',
          duration: 10,
          title: 'Mise en contexte',
          narrative: 'Vous êtes face aux décideurs.',
          clientNarrative:
            'Le directeur général et la directrice financière vous observent.',
          clientQuestion: 'Qu’attendez-vous de cette réunion ?',
          sellerNote: 'Ne parlez pas tout de suite. Laissez-les s’exprimer.',
          advanceLabel: 'Commencer la réunion',
        },
        {
          type: 'action',
          duration: 30,
          title: 'Premier geste',
          actionIds: ['accueil', 'question_strategique'],
          clientActions: [
            {
              id: 'contexte_client',
              type: 'input',
              prompt: 'Quel défi majeur souhaitez-vous résoudre en priorité ?',
              fieldLabel: 'Votre défi principal',
            },
          ],
          narrative: 'À vous de jouer.',
          clientNarrative: 'Votre réponse va orienter toute la discussion.',
          clientQuestion: 'Pouvez-vous partager votre priorité ?',
          sellerNote: 'Notez ce qu’ils disent : cela servira plus tard.',
          advanceLabel: 'Passer à la suite',
        },
        {
          type: 'consequences',
          duration: 15,
          title: 'Premières réactions',
          narrative: 'Votre accroche a fait son effet.',
          clientNarrative: 'Votre intérêt a été noté.',
          clientQuestion: 'Êtes-vous prêt à en savoir plus ?',
          revealDelta: true,
          advanceLabel: 'Je suis curieux',
        },
      ],
    },

    // Étape 2 : Démonstration de la valeur
    {
      id: 'demonstration_valeur',
      title: 'Démonstration de la valeur',
      clientTitle: 'Que proposez-vous concrètement ?',
      description: 'Nous montrons la solution en action.',
      problematisation: 'Comment notre solution répond-elle à votre défi ?',
      narrativeOverride: 'La démo commence.',
      narrativeArc: {
        position: 'escalade',
        tensionLevel: 2,
        previousStepLink: 'Vous avez capté leur attention.',
        nextStepForeshadow: 'Les questions vont fuser.',
      },
      learningObjectives: [],
      stepSuccessCriteria: {
        minActionsRequired: 2,
        keyActionId: 'demo_produit',
        feedbackIfMissed: 'Sans démo, la valeur reste abstraite.',
      },
      debriefMessage: 'Démonstration terminée.',
      actions: [
        {
          id: 'demo_produit',
          label: 'Lancer la démonstration',
          clientLabel: 'Je regarde la démo',
          weight: 3,
          description: 'Une vidéo de 3 minutes montrant le flux de valeur.',
          effect: { valeur_percue: 25, interet_decisionnaire: 15, profil_global: 25 },
          media: {
            type: 'youtube',
            url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
            title: 'Démonstration de la solution',
          },
        },
        {
          id: 'montrer_capture_ecran',
          label: 'Afficher une capture clé',
          clientLabel: 'Je vois le résultat',
          weight: 2,
          description: 'L’interface finale avec un résultat métier.',
          effect: { valeur_percue: 10, confiance_decisionnaire: 10, profil_global: 10 },
          media: {
            type: 'image',
            url: 'https://via.placeholder.com/900x500?text=Resultat+client',
            title: 'Exemple de résultat concret',
          },
        },
        {
          id: 'garder_silence_apres_demo',
          label: 'Garder le silence après la démo',
          clientLabel: 'Je réfléchis',
          weight: 3,
          description: 'Laisser l’impact s’installer.',
          prerequisites: ['demo_produit'],
          effect: { confiance_decisionnaire: 10, valeur_percue: 5, profil_global: 10 },
        },
      ],
      phases: [
        {
          type: 'observation',
          duration: 8,
          title: 'Préparation de la démo',
          narrative: 'L’écran se prépare.',
          clientNarrative: 'Vous allez voir quelque chose de concret.',
          clientQuestion: 'Qu’espérez-vous découvrir ?',
          sellerNote: 'Ne dites rien, lancez la démo.',
          advanceLabel: 'Lancer la démo',
        },
        {
          type: 'action',
          duration: 45,
          actionIds: ['demo_produit', 'montrer_capture_ecran', 'garder_silence_apres_demo'],
          clientActions: [
            {
              id: 'reaction_demo',
              type: 'choice',
              prompt: 'Qu’est-ce qui vous a le plus marqué ?',
              options: [
                { id: 'marque_interface', label: "L'interface moderne", effect: { valeur_percue: 5 } },
                { id: 'marque_vitesse', label: 'La rapidité du résultat', effect: { interet_decisionnaire: 10 } },
                { id: 'marque_rien', label: 'Rien de spécial', effect: { friction_interne: 5 } },
              ],
            },
            {
              id: 'question_demo',
              type: 'input',
              prompt: 'Avez-vous une question sur ce que vous venez de voir ?',
              fieldLabel: 'Votre question',
            },
          ],
          narrative: 'La démo est terminée. Observez les réactions.',
          clientNarrative: 'Vous venez de vivre une expérience.',
          clientQuestion: 'Qu’est-ce qui vous vient à l’esprit ?',
          sellerNote: 'Les micro-expressions comptent autant que les mots.',
          advanceLabel: 'La salle réagit',
        },
        {
          type: 'consequences',
          duration: 15,
          title: 'Retour à chaud',
          narrative: 'Les premières réactions sont enregistrées.',
          clientNarrative: 'Votre ressenti est pris en compte.',
          clientQuestion: 'Cela répond-il à votre défi ?',
          revealDelta: true,
          advanceLabel: 'Continuer',
        },
      ],
    },

    // Étape 3 : Preuve de rentabilité
    {
      id: 'preuve_roi',
      title: 'Preuve de rentabilité',
      clientTitle: 'Quel est le retour sur investissement ?',
      description: 'Nous présentons les chiffres.',
      problematisation: 'Pouvons-nous justifier cet investissement ?',
      narrativeOverride: 'La DAF prend la parole.',
      narrativeArc: {
        position: 'pivot',
        tensionLevel: 3,
        previousStepLink: "L'envie est là, il faut rassurer.",
        nextStepForeshadow: 'Les objections arrivent.',
      },
      learningObjectives: [],
      stepSuccessCriteria: {
        minActionsRequired: 1,
        keyActionId: 'proposition_roi',
        feedbackIfMissed: 'Sans ROI, pas de décision.',
      },
      debriefMessage: 'ROI présenté.',
      actions: [
        {
          id: 'proposition_roi',
          label: 'Présenter le calculateur de ROI',
          clientLabel: 'Je découvre les gains potentiels',
          weight: 3,
          description: 'Gain net estimé : 340 k€ sur 3 ans.',
          effect: { budget_percu: 25, alignement_strategique: 15, profil_global: 25 },
          media: {
            type: 'image',
            url: 'https://via.placeholder.com/900x500?text=ROI+Calculator',
            title: 'Calculateur de ROI',
          },
        },
        {
          id: 'partager_references',
          label: 'Partager des références clients',
          clientLabel: 'Je consulte les cas clients',
          weight: 2,
          description: 'Deux études de cas dans votre secteur.',
          effect: { confiance_decisionnaire: 15, friction_interne: -10, profil_global: 15 },
        },
      ],
      phases: [
        {
          type: 'observation',
          duration: 8,
          title: 'Attentes financières',
          narrative: '« Quel budget avez-vous alloué à ce type de projet ? »',
          clientNarrative: 'Des questions précises sur les coûts.',
          clientQuestion: 'Quel est le seuil de rentabilité que vous visez ?',
          sellerNote: 'Ne défendez pas le prix, montrez la valeur.',
          advanceLabel: 'Présenter les chiffres',
        },
        {
          type: 'action',
          duration: 40,
          actionIds: ['proposition_roi', 'partager_references'],
          clientActions: [
            {
              id: 'objections_roi',
              type: 'choice',
              prompt: 'Quel point souhaitez-vous creuser ?',
              options: [
                { id: 'question_cout', label: 'Le coût total', effect: { budget_percu: -5, friction_interne: 5 } },
                { id: 'question_delai', label: 'Le délai de mise en œuvre', effect: { urgence_achat: -5 } },
                { id: 'question_resultats', label: 'Les résultats mesurables', effect: { valeur_percue: 10 } },
              ],
            },
            {
              id: 'question_financiere',
              type: 'input',
              prompt: 'Une précision sur l’aspect financier ?',
              fieldLabel: 'Votre question',
            },
          ],
          narrative: 'La balle est dans votre camp.',
          clientNarrative: 'Vous examinez les preuves.',
          clientQuestion: 'Ces chiffres vous rassurent-ils ?',
          sellerNote: 'Si une question vous dépasse, proposez un complément d’information ultérieur.',
          advanceLabel: 'Les chiffres sont clairs',
        },
        {
          type: 'consequences',
          duration: 15,
          title: 'Arbitrage financier',
          narrative: 'Les données financières sont sur la table.',
          clientNarrative: 'Votre position se précise.',
          clientQuestion: 'Où en êtes-vous ?',
          revealDelta: true,
          advanceLabel: 'Aborder la suite',
        },
      ],
    },

    // Étape 4 : Gestion des objections
    {
      id: 'gestion_objections',
      title: 'Réponses aux objections',
      clientTitle: 'Des réserves demeurent',
      description: 'Nous levons les derniers freins.',
      problematisation: 'Qu’est-ce qui pourrait vous empêcher d’avancer ?',
      narrativeOverride: 'La tension monte légèrement.',
      narrativeArc: {
        position: 'climax',
        tensionLevel: 4,
        previousStepLink: 'Les deux parties ont des arguments.',
        nextStepForeshadow: 'Un terrain d’entente est possible.',
      },
      learningObjectives: [],
      stepSuccessCriteria: {
        minActionsRequired: 1,
        keyActionId: 'repondre_objection_technique',
        feedbackIfMissed: 'Les objections non traitées tuent la vente.',
      },
      debriefMessage: 'Objections traitées.',
      actions: [
        {
          id: 'repondre_objection_technique',
          label: 'Répondre sur la sécurité / intégration',
          clientLabel: 'Je suis rassuré techniquement',
          weight: 3,
          description: 'Notre API est certifiée SOC2.',
          effect: { confiance_decisionnaire: 15, friction_interne: -15, profil_global: 15 },
        },
        {
          id: 'repondre_objection_prix',
          label: 'Justifier le prix par la valeur',
          clientLabel: 'Je comprends le prix',
          weight: 3,
          description: 'Le coût total est amorti en 14 mois.',
          effect: { budget_percu: 15, friction_interne: -20, profil_global: 20 },
        },
        {
          id: 'proposer_clause_securite',
          label: 'Proposer une clause de garantie',
          clientLabel: 'J’obtiens une sécurité',
          weight: 3,
          description: 'Satisfait ou remboursé sous 3 mois.',
          effect: { alignement_strategique: 20, friction_interne: -15, urgence_achat: 10, profil_global: 25 },
        },
      ],
      phases: [
        {
          type: 'observation',
          duration: 10,
          title: 'Expression des doutes',
          narrative: '« Et si ça ne marche pas ? »',
          clientNarrative: 'Des inquiétudes légitimes s’expriment.',
          clientQuestion: 'Qu’est-ce qui vous inquiète ?',
          sellerNote: 'Écoutez sans interrompre.',
          advanceLabel: 'Je réponds',
        },
        {
          type: 'action',
          duration: 40,
          actionIds: ['repondre_objection_technique', 'repondre_objection_prix', 'proposer_clause_securite'],
          clientActions: [
            {
              id: 'choix_objection',
              type: 'choice',
              prompt: 'Quel point souhaitez-vous que nous traitions en priorité ?',
              options: [
                { id: 'obj_technique', label: 'Sécurité / Intégration', effect: { confiance_decisionnaire: 5 } },
                { id: 'obj_prix', label: 'Le prix', effect: { budget_percu: 5 } },
                { id: 'obj_accompagnement', label: "L'accompagnement", effect: { alignement_strategique: 5 } },
              ],
            },
            {
              id: 'objection_personnalisee',
              type: 'input',
              prompt: 'Une autre objection ?',
              fieldLabel: 'Votre préoccupation',
            },
          ],
          narrative: 'Vous avez la parole pour rassurer.',
          clientNarrative: 'Vos réponses sont cruciales.',
          clientQuestion: 'Ces réponses vous satisfont-elles ?',
          sellerNote: 'Terminez toujours par une question de contrôle (« Est-ce que cela répond à votre préoccupation ? »).',
          advanceLabel: 'Objections levées',
        },
        {
          type: 'consequences',
          duration: 15,
          title: 'Clarification',
          narrative: 'Les nuages se dissipent.',
          clientNarrative: 'Vous vous sentez plus en confiance.',
          clientQuestion: 'Prêt à passer à l’étape suivante ?',
          revealDelta: true,
          advanceLabel: 'Vers la conclusion',
        },
      ],
    },

    // Étape 5 : Négociation et accord
    {
      id: 'negociation_accord',
      title: 'Négociation et engagement',
      clientTitle: 'Discutons des conditions finales',
      description: 'Nous trouvons un accord mutuellement bénéfique.',
      problematisation: 'Quelle modalité vous convient le mieux ?',
      narrativeOverride: 'Le DG et la DAF se concertent.',
      narrativeArc: {
        position: 'resolution',
        tensionLevel: 2,
        previousStepLink: 'Les objections sont levées.',
      },
      learningObjectives: [],
      stepSuccessCriteria: {
        minActionsRequired: 1,
        keyActionId: 'proposer_phasage',
        feedbackIfMissed: 'Sans proposition concrète, l’élan retombe.',
      },
      debriefMessage: 'Accord trouvé.',
      actions: [
        {
          id: 'proposer_phasage',
          label: 'Proposer un déploiement progressif',
          clientLabel: 'J’envisage un phasage',
          weight: 3,
          exclusiveGroup: 'modalite',
          description: 'Phase 1 : pilote, Phase 2 : déploiement.',
          effect: { urgence_achat: 15, alignement_strategique: 10, friction_interne: -10, profil_global: 20 },
        },
        {
          id: 'proposer_offre_limitee',
          label: 'Offre valable 7 jours',
          clientLabel: 'Je note l’urgence',
          weight: 2,
          exclusiveGroup: 'modalite',
          description: 'Remise de 10 % si signature cette semaine.',
          effect: { urgence_achat: 25, budget_percu: 10, profil_global: 20 },
        },
        {
          id: 'signer_contrat',
          label: 'Présenter le contrat',
          clientLabel: 'Je lis le contrat',
          weight: 3,
          description: 'Document prêt à signer.',
          effect: { probabilite_accord: 50, valeur_percue: 15, profil_global: 40 },
        },
      ],
      phases: [
        {
          type: 'observation',
          duration: 8,
          title: 'Dernières hésitations',
          narrative: 'Les regards se croisent.',
          clientNarrative: 'Vous pesez le pour et le contre.',
          clientQuestion: 'Qu’est-ce qui pourrait vous décider ?',
          sellerNote: 'Soyez prêt à faire une concession mineure.',
          advanceLabel: 'Faire une proposition',
        },
        {
          type: 'action',
          duration: 35,
          actionIds: ['proposer_phasage', 'proposer_offre_limitee', 'signer_contrat'],
          clientActions: [
            {
              id: 'decision_finale',
              type: 'choice',
              prompt: 'Quelle modalité préférez-vous ?',
              options: [
                { id: 'choix_phasage', label: 'Déploiement progressif', effect: { probabilite_accord: 10 } },
                { id: 'choix_offre', label: "Offre limitée", effect: { probabilite_accord: 15 } },
                { id: 'choix_attendre', label: 'Je souhaite réfléchir encore', effect: { probabilite_accord: -20 } },
              ],
            },
          ],
          narrative: 'Le moment de vérité.',
          clientNarrative: 'Vous avez toutes les cartes en main.',
          clientQuestion: 'Quelle est votre décision ?',
          sellerNote: 'Silence après la proposition. Laissez-les décider.',
          advanceLabel: 'Décision prise',
        },
        {
          type: 'consequences',
          duration: 15,
          title: 'Signature',
          narrative: 'Un stylo circule.',
          clientNarrative: 'Votre décision est enregistrée.',
          clientQuestion: 'Comment voulez-vous communiquer en interne ?',
          revealDelta: true,
          advanceLabel: 'Voir le résumé',
        },
      ],
    },
  ],

  // ── Moteur de simulation ──────────────────────────────────────────────────
  simulationEngine: {
    dynamics: [
      {
        parameter: 'interet_decisionnaire',
        formula:
          '0.85 * interet_decisionnaire + 0.15 * valeur_percue + 10 * (question_strategique ? 1 : 0)',
        fallback: 25,
        clampMin: 0,
        clampMax: 100,
        type: 'nonlinear',
        periodicity: 1,
        reportLabel: 'Intérêt du DG',
      },
      {
        parameter: 'confiance_decisionnaire',
        formula:
          '0.9 * confiance_decisionnaire + 0.1 * (interet_decisionnaire * 0.5 + demo_produit * 15)',
        fallback: 15,
        clampMin: 0,
        clampMax: 100,
        type: 'logistic',
        periodicity: 2,
        reportLabel: 'Confiance',
      },
      {
        parameter: 'budget_percu',
        formula:
          '0.8 * budget_percu + 0.2 * valeur_percue + 8 * (proposition_roi ? 1 : 0)',
        fallback: 20,
        clampMin: 0,
        clampMax: 100,
        type: 'linear',
        periodicity: 2,
        reportLabel: 'Budget perçu',
      },
      {
        parameter: 'friction_interne',
        formula:
          'Math.max(0, friction_interne - 3) + 10 * (interet_decisionnaire > 60 && budget_percu < 40 ? 1 : 0)',
        fallback: 15,
        clampMin: 0,
        clampMax: 100,
        type: 'linear',
        periodicity: 1,
        reportLabel: 'Friction interne',
      },
      {
        parameter: 'alignement_strategique',
        formula:
          '0.5 * (interet_decisionnaire + budget_percu) * 0.5 - 0.3 * friction_interne + 15 * (clause_securite ? 1 : 0)',
        fallback: 20,
        clampMin: 0,
        clampMax: 100,
        type: 'nonlinear',
        periodicity: 2,
        reportLabel: 'Alignement stratégique',
      },
      {
        parameter: 'probabilite_accord',
        formula:
          '0.3 * interet_decisionnaire + 0.2 * confiance_decisionnaire + 0.2 * budget_percu + 0.2 * alignement_strategique + 0.1 * urgence_achat - 0.2 * friction_interne',
        fallback: 10,
        clampMin: 0,
        clampMax: 100,
        type: 'logistic',
        periodicity: 1,
        reportLabel: 'Probabilité d’accord',
      },
    ],
    computedMetrics: {
      question_strategique: "state.completedActions.includes('question_strategique') ? 1 : 0",
      demo_produit: "state.completedActions.includes('demo_produit') ? 1 : 0",
      proposition_roi: "state.completedActions.includes('proposition_roi') ? 1 : 0",
      clause_securite: "state.completedActions.includes('proposer_clause_securite') ? 1 : 0",
    },
    network: [
      {
        source: 'Direction Générale',
        target: 'Contrôle Financier',
        type: 'influence',
        strength: 0.7,
        evolving: true,
      },
      {
        source: 'Contrôle Financier',
        target: 'Direction Générale',
        type: 'frein',
        strength: -0.5,
        evolving: true,
      },
    ],
    quotes: [
      '« On n’achète pas un produit, on achète un résultat. »',
      '« La confiance se gagne en un silence après une démo. »',
      '« Un chiffre précis vaut mieux qu’un enthousiasme vague. »',
    ],
    predictionModel: { enabled: true, method: 'bayesian', horizon: 6 },
    calibration: { enabled: false },
  },

  // ── Solutions types ────────────────────────────────────────────────────────
  solutions: [
    {
      name: 'Signature immédiate',
      description: 'Contrat signé en séance.',
      difficulty: 'expert',
      requiredActions: [
        'question_strategique',
        'demo_produit',
        'garder_silence_apres_demo',
        'proposition_roi',
        'repondre_objection_prix',
        'proposer_phasage',
        'signer_contrat',
      ],
      expectedMetrics: {
        interet_decisionnaire: 85,
        budget_percu: 80,
        alignement_strategique: 80,
        probabilite_accord: 92,
        profil_global: 180,
      },
      interpretation: {
        shortSummary: 'Parcours complet : écoute, valeur, preuve, déblocage, engagement.',
        keyInsight: 'La question stratégique initiale a tout aligné.',
        theoreticalPromise: 95,
        realWorldProbability: 70,
        tone: 'radieux',
        competencies: [
          {
            name: 'Écoute active',
            level: 'Expert',
            evidence: 'Question stratégique, silence après démo.',
          },
          {
            name: 'Argumentation chiffrée',
            level: 'Expert',
            evidence: 'ROI détaillé, réponse aux objections.',
          },
        ],
      },
    },
    {
      name: 'Accord de principe',
      description: 'Lettre d’intention, contrat à suivre.',
      difficulty: 'avancé',
      requiredActions: ['question_strategique', 'demo_produit', 'proposition_roi', 'proposer_phasage'],
      expectedMetrics: { probabilite_accord: 65, profil_global: 120 },
      interpretation: {
        shortSummary: 'Bon pitch, quelques réserves persistent.',
        keyInsight: 'Une objection prix non totalement levée.',
        theoreticalPromise: 80,
        realWorldProbability: 50,
        tone: 'lucide',
      },
    },
  ],

  // ── Contrefactuels ─────────────────────────────────────────────────────────
  counterfactuals: [
    {
      actionId: 'question_strategique',
      actionTitle: 'Question stratégique',
      description: 'Sans elle, le projet reste une boîte noire.',
      deltaProbability: 0.35,
    },
    {
      actionId: 'garder_silence_apres_demo',
      actionTitle: 'Silence après démo',
      description: 'Le temps de l’assimilation est crucial.',
      deltaProbability: 0.25,
    },
    {
      actionId: 'proposer_clause_securite',
      actionTitle: 'Clause de garantie',
      description: 'C’est ce qui a rassuré la DAF.',
      deltaProbability: 0.30,
    },
  ],

  // ── Chemin optimal ─────────────────────────────────────────────────────────
  optimalPath: {
    actions: [
      'accueil',
      'question_strategique',
      'demo_produit',
      'garder_silence_apres_demo',
      'montrer_capture_ecran',
      'proposition_roi',
      'partager_references',
      'repondre_objection_technique',
      'repondre_objection_prix',
      'proposer_clause_securite',
      'proposer_phasage',
      'signer_contrat',
    ],
    predictedMetrics: {
      probabilite_accord: 94,
      interet_decisionnaire: 88,
      budget_percu: 82,
      alignement_strategique: 85,
      profil_global: 195,
    },
    probabilityOfSuccess: 0.94,
    generationFound: 1,
  },

  // ── Cartographie du processus ──────────────────────────────────────────────
  processMap: {
    nodes: [
      { id: 'start', label: 'Connexion' },
      { id: 'valeur', label: 'Valeur démontrée' },
      { id: 'roi', label: 'ROI prouvé' },
      { id: 'objections', label: 'Objections levées' },
      { id: 'accord', label: 'Accord signé' },
    ],
    edges: [
      { from: 'start', to: 'valeur' },
      { from: 'valeur', to: 'roi' },
      { from: 'roi', to: 'objections' },
      { from: 'objections', to: 'accord' },
    ],
  },

  optimizationGoals: { primary: 'maximiser probabilite_accord' },
};

const TEMPLATES = { service_prestation: TPL_SERVICE, TPL_GENERIC_SALES };

// ═══════════════════════════════════════════════════════════════════════════════
// 3. PERSISTENCE
// ═══════════════════════════════════════════════════════════════════════════════

const useBuilderPersistence = (slug) => {
  const key = `commercial_builder_${slug || 'draft'}`;
  const load = () => { try { const r = localStorage.getItem(key); return r ? JSON.parse(r) : null; } catch { return null; } };
  const save = (d) => { try { localStorage.setItem(key, JSON.stringify(d)); } catch {} };
  return { load, save };
};

// ═══════════════════════════════════════════════════════════════════════════════
// 4. COMPOSANTS ATOMIQUES — même palette que CommercialCase
// ═══════════════════════════════════════════════════════════════════════════════

// Champ texte
const Field = ({ label, required, hint, children, theme }) => (
  <div className="space-y-1.5">
    <label className="flex items-center gap-1.5">
      <span className="text-[10px] font-mono tracking-[0.2em] uppercase" style={{ color: `${theme.ink}55` }}>{label}</span>
      {required && <span style={{ color: theme.accent }}>*</span>}
    </label>
    {children}
    {hint && <p className="text-[9px] font-mono" style={{ color: `${theme.ink}35` }}>{hint}</p>}
  </div>
);

const Input = ({ value, onChange, placeholder, type = 'text', theme, mono, small, ...rest }) => (
  <input
    type={type} value={value || ''} onChange={onChange} placeholder={placeholder}
    className="w-full rounded-lg outline-none transition-all"
    style={{
      padding: small ? '6px 10px' : '8px 12px',
      fontSize: small ? '11px' : '13px',
      fontFamily: mono ? '"DM Mono", monospace' : '"DM Sans", sans-serif',
      background: `${theme.ink}05`, border: `1px solid ${theme.ink}12`, color: theme.ink,
    }}
    onFocus={e => { e.target.style.borderColor = `${theme.accent}55`; e.target.style.background = `${theme.ink}03`; }}
    onBlur={e => { e.target.style.borderColor = `${theme.ink}12`; e.target.style.background = `${theme.ink}05`; }}
    {...rest}
  />
);

const Textarea = ({ value, onChange, placeholder, rows = 3, theme }) => (
  <textarea
    value={value || ''} onChange={onChange} placeholder={placeholder} rows={rows}
    className="w-full rounded-lg outline-none transition-all resize-none"
    style={{ padding: '8px 12px', fontSize: '13px', fontFamily: '"DM Sans", sans-serif', background: `${theme.ink}05`, border: `1px solid ${theme.ink}12`, color: theme.ink }}
    onFocus={e => { e.target.style.borderColor = `${theme.accent}55`; }}
    onBlur={e => { e.target.style.borderColor = `${theme.ink}12`; }}
  />
);

const Sel = ({ value, onChange, options, theme, small }) => (
  <select
    value={value} onChange={onChange}
    className="w-full rounded-lg outline-none transition-all"
    style={{ padding: small ? '6px 10px' : '8px 12px', fontSize: small ? '11px' : '13px', fontFamily: '"DM Sans", sans-serif', background: `${theme.ink}05`, border: `1px solid ${theme.ink}12`, color: theme.ink }}
  >
    {options.map(({ value: v, label }) => <option key={v} value={v}>{label}</option>)}
  </select>
);

const Btn = ({ children, onClick, variant = 'default', icon: Icon, small, theme, disabled, type = 'button' }) => {
  const map = {
    primary: { bg: theme.accent,         color: '#fff',          border: 'none'                                },
    default: { bg: `${theme.ink}07`,     color: `${theme.ink}70`, border: `1px solid ${theme.ink}12`          },
    ghost:   { bg: 'transparent',        color: `${theme.ink}55`, border: `1px solid ${theme.ink}10`          },
    danger:  { bg: '#ef444410',          color: '#ef4444',        border: '1px solid #ef444425'                },
    accent:  { bg: `${theme.accent}10`,  color: theme.accent,     border: `1px solid ${theme.accent}25`       },
    success: { bg: `${theme.success}10`, color: theme.success,    border: `1px solid ${theme.success}25`      },
  };
  const s = map[variant] || map.default;
  return (
    <button
      type={type} onClick={onClick} disabled={disabled}
      className="inline-flex items-center gap-2 rounded-lg font-mono transition-all shrink-0"
      style={{ padding: small ? '5px 10px' : '7px 14px', fontSize: small ? '10px' : '11px', letterSpacing: '0.07em', textTransform: 'uppercase', background: s.bg, color: s.color, border: s.border, opacity: disabled ? 0.45 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
    >
      {Icon && <Icon size={small ? 11 : 13} />}
      {children}
    </button>
  );
};

// ─── Ligne rétractable générique ─────────────────────────────────────────────
//
// Affiche un résumé sur une ligne.
// Si `autoCollapse` est true et qu'il y a > 2 items, se replie automatiquement.
// L'utilisateur expand au clic.

const CollapsibleRow = ({ summary, count, icon: Icon, expanded, onToggle, theme, children, badge, autoCollapse = true }) => {
  const shouldCollapse = autoCollapse && count > 2;

  return (
    <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${theme.panelBorder}` }}>
      {/* Ligne résumé */}
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 text-left transition-all"
        style={{ background: expanded ? `${theme.accent}05` : theme.panelBg }}
        onMouseEnter={e => { if (!expanded) e.currentTarget.style.background = theme.rowHover; }}
        onMouseLeave={e => { if (!expanded) e.currentTarget.style.background = theme.panelBg; }}
      >
        {Icon && <Icon size={14} style={{ color: expanded ? theme.accent : `${theme.ink}35`, flexShrink: 0 }} />}
        <span className="flex-1 text-[13px] font-medium" style={{ color: theme.ink, fontFamily: '"DM Sans", sans-serif' }}>
          {summary}
        </span>
        {badge && (
          <span className="px-2 py-0.5 rounded text-[9px] font-mono" style={{ background: `${theme.accent}12`, color: theme.accent }}>
            {badge}
          </span>
        )}
        {count !== undefined && (
          <span className="text-[10px] font-mono" style={{ color: `${theme.ink}35` }}>
            {count} {count <= 1 ? 'élément' : 'éléments'}
          </span>
        )}
        {expanded
          ? <ChevronUp size={14} style={{ color: `${theme.ink}30`, flexShrink: 0 }} />
          : <ChevronDown size={14} style={{ color: `${theme.ink}30`, flexShrink: 0 }} />
        }
      </button>

      {/* Contenu */}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18, ease: 'easeInOut' }}
            className="overflow-hidden"
            style={{ borderTop: `1px solid ${theme.panelBorder}` }}
          >
            <div className="p-4">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── Mini-badge statut ────────────────────────────────────────────────────────
const StatusDot = ({ ok, theme }) => (
  <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: ok ? theme.success : `${theme.ink}20`, display: 'inline-block' }} />
);

// ═══════════════════════════════════════════════════════════════════════════════
// 5. SECTIONS ÉDITABLES
// ═══════════════════════════════════════════════════════════════════════════════

// ─── Section : Identité ───────────────────────────────────────────────────────
const SectionIdentity = ({ scenario, update, theme, themeKey }) => (
  <div className="space-y-4 max-w-lg">
    <Field label="Titre" required theme={theme}>
      <Input value={scenario.title} onChange={e => update('title', e.target.value)} placeholder="Ex : Vente d'un documentaire à une chaîne TV" theme={theme} />
    </Field>
    <Field label="Proposition de valeur" required theme={theme}>
      <Textarea value={scenario.pedagogicalGoal} onChange={e => update('pedagogicalGoal', e.target.value)} placeholder="Ce que cette simulation démontre ou convainc…" rows={3} theme={theme} />
    </Field>
    <div className="grid grid-cols-2 gap-4">
      <Field label="Slug URL" required theme={theme} hint="Minuscules, tirets uniquement">
        <Input value={scenario.slug} onChange={e => update('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-'))} placeholder="vente-documentaire" theme={theme} mono />
      </Field>
      <Field label="Thème visuel" theme={theme}>
        <Sel value={scenario.theme} onChange={e => update('theme', e.target.value)} options={[{ value: 'light', label: 'Pitch Room clair' }, { value: 'dark', label: 'Sombre' }]} theme={theme} />
      </Field>
    </div>
    <Field label="Mode d'affichage" theme={theme} themeKey={themeKey} hint="Pilote le vocabulaire commercial vs pédagogique (VOCAB_MAP dans CommercialCase)">
      <Sel value={scenario.displayMode} onChange={e => update('displayMode', e.target.value)} options={[{ value: 'vente', label: 'Vente' }, { value: 'pedagogique', label: 'Formation' }]} theme={theme} />
    </Field>
  </div>
);

// ─── Section : Partie prenante (une ligne) ────────────────────────────────────
const FamilyRow = ({ fam, onUpdate, onRemove, theme }) => {
  const [open, setOpen] = useState(!fam.name);
  const valid = fam.name.trim();

  return (
    <CollapsibleRow
      summary={fam.name || <span style={{ color: `${theme.ink}35` }}>Partie prenante sans nom</span>}
      icon={Users}
      expanded={open}
      onToggle={() => setOpen(v => !v)}
      theme={theme}
      badge={fam.isToxic ? 'Résistance' : fam.isActive ? 'Actif' : 'Inactif'}
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Nom" required theme={theme}><Input value={fam.name} onChange={e => onUpdate('name', e.target.value)} placeholder="Directeur de programmes" theme={theme} /></Field>
          <Field label="Rôle narratif" theme={theme}><Input value={fam.narrativeRole || ''} onChange={e => onUpdate('narrativeRole', e.target.value)} placeholder="Décide du budget final" theme={theme} /></Field>
          <Field label="Agents (JSON)" theme={theme} hint="Tableau d'agents avec reactionsTo">
  <Textarea
    value={JSON.stringify(fam.agents || [], null, 2)}
    onChange={e => {
      try {
        const agents = JSON.parse(e.target.value);
        onUpdate('agents', agents);
      } catch { /* ignore */ }
    }}
    rows={6}
    theme={theme}
  />
</Field>
        </div>
        <div className="flex items-center gap-6">
          {[['isActive', 'Actif dès le départ'], ['isToxic', 'Force de résistance']].map(([k, l]) => (
            <label key={k} className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!fam[k]} onChange={e => onUpdate(k, e.target.checked)} />
              <span className="text-[12px]" style={{ color: `${theme.ink}60`, fontFamily: '"DM Sans", sans-serif' }}>{l}</span>
            </label>
          ))}
          <button onClick={onRemove} className="ml-auto" style={{ color: `${theme.ink}25` }} onMouseEnter={e => e.currentTarget.style.color = theme.error} onMouseLeave={e => e.currentTarget.style.color = `${theme.ink}25`}>
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </CollapsibleRow>
  );
};

// ─── Section : Indicateur (une ligne) ────────────────────────────────────────
const IndicatorRow = ({ k, val, onUpdate, onRemove, theme }) => (
  <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl" style={{ background: `${theme.ink}04`, border: `1px solid ${theme.ink}08` }}>
    <span className="flex-1 text-[12px] font-mono" style={{ color: `${theme.ink}65` }}>{k.replace(/_/g, ' ')}</span>
    <div className="w-32 h-1 rounded-full overflow-hidden" style={{ background: `${theme.ink}12` }}>
      <div className="h-full rounded-full transition-all" style={{ width: `${val}%`, background: theme.accent }} />
    </div>
    <input
      type="number" value={val} min={0} max={100}
      onChange={e => onUpdate(Number(e.target.value))}
      className="w-16 rounded text-right text-[12px] font-mono outline-none px-2 py-1"
      style={{ background: `${theme.ink}07`, border: `1px solid ${theme.ink}10`, color: theme.ink }}
    />
    <span className="text-[10px] font-mono" style={{ color: `${theme.ink}30` }}>%</span>
    <button onClick={onRemove} style={{ color: `${theme.ink}20` }} onMouseEnter={e => e.currentTarget.style.color = theme.error} onMouseLeave={e => e.currentTarget.style.color = `${theme.ink}20`}>
      <Trash2 size={13} />
    </button>
  </div>
);

// ─── Section : Action (collapsible) ──────────────────────────────────────────
const ActionRow = ({ action, onChange, onRemove, theme }) => {
  const [open, setOpen] = useState(!action.id);
  const valid = action.id && action.label;

  // Effets : string "cle:val, cle:val" <-> objet
  const effectStr = Object.entries(action.effect || {}).map(([k, v]) => `${k}:${v}`).join(', ');
  const parseEffect = (raw) => {
    const obj = {};
    raw.split(',').forEach(p => { const [k, v] = p.split(':').map(s => s.trim()); if (k && v !== undefined && !isNaN(v)) obj[k] = Number(v); });
    return obj;
  };

  return (
    <CollapsibleRow
      summary={action.label || <span style={{ color: `${theme.ink}35` }}>Action sans titre</span>}
      icon={Zap}
      expanded={open}
      onToggle={() => setOpen(v => !v)}
      theme={theme}
      badge={action.id || null}
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Identifiant" required theme={theme} hint="minuscules_tirets_bas">
            <Input value={action.id} onChange={e => onChange({ ...action, id: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })} placeholder="ecoute_active" theme={theme} mono />
          </Field>
          <Field label="Label affiché" required theme={theme}>
            <Input value={action.label} onChange={e => onChange({ ...action, label: e.target.value })} placeholder="Ex : Poser les bonnes questions" theme={theme} />
          </Field>
          <Field label="Label client (clientLabel)" theme={theme} hint="Affiché en mode vente à la place du label standard">
            <Input value={action.clientLabel || ''} onChange={e => onChange({ ...action, clientLabel: e.target.value })} placeholder="Ex : Je découvre le sujet" theme={theme} />
          </Field>
        </div>
        <Field label="Description" theme={theme}>
          <Textarea value={action.description || ''} onChange={e => onChange({ ...action, description: e.target.value })} rows={2} theme={theme} />
        </Field>
        <Field label="Groupe exclusif (exclusiveGroup)" theme={theme} hint="Si plusieurs actions partagent ce nom, une seule pourra être choisie.">
  <Input value={action.exclusiveGroup || ''} onChange={e => onChange({ ...action, exclusiveGroup: e.target.value })} placeholder="ex: type_documentaire" theme={theme} mono />
</Field>
<Field label="Citation (quote)" theme={theme} hint="Affichée en toast live après l'action.">
  <Textarea value={action.quote || ''} onChange={e => onChange({ ...action, quote: e.target.value })} placeholder="« Une citation inspirante. » – Auteur" rows={2} theme={theme} />
</Field>
        <Field label="Effets sur les indicateurs" theme={theme} hint="Format : cle:valeur, cle:valeur — valeurs négatives acceptées">
          <Input value={effectStr} onChange={e => onChange({ ...action, effect: parseEffect(e.target.value) })} placeholder="confiance_client:12, valeur_percue:8" theme={theme} mono />
        </Field>

        {/* Feedback commercial */}
        <div className="pt-1">
          <p className="text-[10px] font-mono tracking-[0.2em] uppercase mb-3" style={{ color: theme.accent }}>Feedback commercial</p>
          <div className="grid grid-cols-2 gap-3">
            {[
              { k: 'whatHappened', l: 'Ce que le client perçoit' },
              { k: 'whyItMatters',  l: 'Pourquoi ça décide' },
              { k: 'whatItCosts',   l: 'Risque si mal exécuté' },
              { k: 'boostCondition',l: "Levier d'amplification" },
            ].map(({ k, l }) => (
              <Field key={k} label={l} theme={theme}>
                <Textarea value={action.deepFeedback?.[k] || ''} onChange={e => onChange({ ...action, deepFeedback: { ...(action.deepFeedback || {}), [k]: e.target.value } })} rows={2} theme={theme} />
              </Field>
            ))}
          </div>
        </div>

        {/* Signal de confiance */}
        <div>
          <p className="text-[10px] font-mono tracking-[0.2em] uppercase mb-3" style={{ color: theme.accent }}>Signal de confiance client</p>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Compétence" theme={theme}><Input value={action.competencySignal?.name || ''} onChange={e => onChange({ ...action, competencySignal: { ...(action.competencySignal || {}), name: e.target.value } })} placeholder="Intelligence relationnelle" theme={theme} /></Field>
            <Field label="Niveau" theme={theme}>
              <Sel value={action.competencySignal?.level || 'intermédiaire'} onChange={e => onChange({ ...action, competencySignal: { ...(action.competencySignal || {}), level: e.target.value } })} options={[{ value: 'débutant', label: 'Débutant' }, { value: 'intermédiaire', label: 'Intermédiaire' }, { value: 'avancé', label: 'Avancé' }, { value: 'expert', label: 'Expert' }]} theme={theme} />
            </Field>
            <Field label="Descripteur" theme={theme}><Input value={action.competencySignal?.descriptor || ''} onChange={e => onChange({ ...action, competencySignal: { ...(action.competencySignal || {}), descriptor: e.target.value } })} placeholder="Sait créer la confiance…" theme={theme} /></Field>
          </div>
        </div>

        {/* Média */}
        <div>
          <p className="text-[10px] font-mono tracking-[0.2em] uppercase mb-3" style={{ color: theme.accent }}>Média embarqué (optionnel)</p>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Type" theme={theme}>
              <Sel value={action.media?.type || 'none'} onChange={e => { const t = e.target.value; onChange({ ...action, media: t === 'none' ? null : { type: t, url: '', title: '' } }); }} options={[{ value: 'none', label: 'Aucun' }, { value: 'youtube', label: 'YouTube' }, { value: 'image', label: 'Image URL' }]} theme={theme} />
            </Field>
            {action.media && (<>
              <Field label="URL" theme={theme}><Input value={action.media.url || ''} onChange={e => onChange({ ...action, media: { ...action.media, url: e.target.value } })} placeholder={action.media.type === 'youtube' ? 'https://youtube.com/watch?v=…' : 'https://…'} theme={theme} mono small /></Field>
              <Field label="Titre" theme={theme}><Input value={action.media.title || ''} onChange={e => onChange({ ...action, media: { ...action.media, title: e.target.value } })} placeholder="Bande-annonce" theme={theme} /></Field>
            </>)}
          </div>
        </div>

        <div className="flex justify-end pt-1">
          <Btn onClick={onRemove} variant="danger" icon={Trash2} small theme={theme}>Supprimer l'action</Btn>
        </div>
      </div>
    </CollapsibleRow>
  );
};

// ─── Section : Phase (collapsible) ───────────────────────────────────────────
const PhaseRow = ({ phase, onChange, onRemove, theme, stepActionIds }) => {
  const [open, setOpen] = useState(false);
  const TYPE_OPTIONS = ['observation', 'action', 'consequences', 'tension', 'bifurcation'].map(v => ({ value: v, label: v.charAt(0).toUpperCase() + v.slice(1) }));

  return (
    <CollapsibleRow
      summary={phase.title || phase.type || <span style={{ color: `${theme.ink}35` }}>Phase sans titre</span>}
      icon={Activity}
      expanded={open}
      onToggle={() => setOpen(v => !v)}
      theme={theme}
      badge={phase.type}
    >
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-3">
          <Field label="Type" theme={theme}>
            <Sel value={phase.type || 'action'} onChange={e => onChange({ ...phase, type: e.target.value })} options={TYPE_OPTIONS} theme={theme} />
          </Field>
          <Field label="Titre" theme={theme}><Input value={phase.title || ''} onChange={e => onChange({ ...phase, title: e.target.value })} placeholder="Ex : Diagnostic client" theme={theme} /></Field>
          <Field label="Durée (secondes)" theme={theme}><Input value={phase.duration || 30} onChange={e => onChange({ ...phase, duration: Number(e.target.value) })} type="number" theme={theme} mono /></Field>
        </div>
        <Field label="Narration" theme={theme}>
          <Textarea value={phase.narrative || ''} onChange={e => onChange({ ...phase, narrative: e.target.value })} placeholder="Texte narratif affiché dans l'overlay pendant cette phase" rows={2} theme={theme} />
        </Field>
        <Field
          label="Contexte théorique (theoreticalHook)"
          theme={theme}
          hint="Affiché dans CommercialOverlay section 'Contexte' — visible en mode solo, masqué en mode live client. Champ natif du script backend, aucune modif API."
        >
          <Textarea value={phase.theoreticalHook || ''} onChange={e => onChange({ ...phase, theoreticalHook: e.target.value })} placeholder="Ex : L'écoute précède toujours la solution dans une vente consultative." rows={2} theme={theme} />
        </Field>
        {phase.type === 'action' && (
          <Field label="IDs des actions (séparés par des virgules)" theme={theme} hint={`Actions disponibles dans cette étape : ${stepActionIds.join(', ') || 'aucune encore'}`}>
            <Input value={(phase.actionIds || []).join(', ')} onChange={e => onChange({ ...phase, actionIds: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} placeholder="ecoute_active, demo_live" theme={theme} mono />
          </Field>
        )}
        <Field label="Indice (hint)" theme={theme} hint="Visible en mode solo uniquement">
          <Input value={phase.hint || ''} onChange={e => onChange({ ...phase, hint: e.target.value })} placeholder="L'écoute avant la solution — toujours." theme={theme} />
        </Field>
        <Field label="Narration client (clientNarrative)" theme={theme} hint="Texte affiché au client en mode vente">
          <Textarea value={phase.clientNarrative || ''} onChange={e => onChange({ ...phase, clientNarrative: e.target.value })} placeholder="Ex : Bonjour et bienvenue..." rows={2} theme={theme} />
        </Field>
        <Field label="Question au client (clientQuestion)" theme={theme}>
          <Input value={phase.clientQuestion || ''} onChange={e => onChange({ ...phase, clientQuestion: e.target.value })} placeholder="Ex : Êtes-vous prêt à découvrir ce sujet ?" theme={theme} />
        </Field>
        <Field label="Note vendeur (sellerNote)" theme={theme} hint="Visible uniquement en mode solo">
          <Textarea value={phase.sellerNote || ''} onChange={e => onChange({ ...phase, sellerNote: e.target.value })} placeholder="Conseil discret pour le vendeur..." rows={2} theme={theme} />
        </Field>
        <Field label="Libellé du bouton continuer (advanceLabel)" theme={theme}>
          <Input value={phase.advanceLabel || ''} onChange={e => onChange({ ...phase, advanceLabel: e.target.value })} placeholder="Ex : J'ai découvert le sujet" theme={theme} />
        </Field>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!phase.revealDelta} onChange={e => onChange({ ...phase, revealDelta: e.target.checked })} />
            <span className="text-[12px]" style={{ color: `${theme.ink}60`, fontFamily: '"DM Sans", sans-serif' }}>Révéler les deltas de métriques en fin de phase</span>
          </label>
          <button onClick={onRemove} className="ml-auto" style={{ color: `${theme.ink}25` }} onMouseEnter={e => e.currentTarget.style.color = theme.error} onMouseLeave={e => e.currentTarget.style.color = `${theme.ink}25`}>
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </CollapsibleRow>
  );
};

// ─── Section : Étape complète ─────────────────────────────────────────────────
const StepRow = ({ step, index, onChange, onRemove, theme }) => {
  const [open, setOpen] = useState(index === 0);
  const actionIds = (step.actions || []).map(a => a.id).filter(Boolean);

  const ARC_POSITIONS = ['exposition', 'montée', 'pivot', 'climax', 'résolution'].map(v => ({ value: v, label: v.charAt(0).toUpperCase() + v.slice(1) }));
  const TENSION = [1,2,3,4,5].map(n => ({ value: String(n), label: `${n} — ${['Stable','Actif','Tendu','Critique','Rupture'][n-1]}` }));

  const addAction = () => onChange({ ...step, actions: [...(step.actions || []), { id: '', label: '', description: '', effect: {}, deepFeedback: {}, competencySignal: { name: '', level: 'intermédiaire', descriptor: '' }, causalChain: [], media: null }] });
  const addPhase  = () => onChange({ ...step, phases: [...(step.phases || []), { type: 'action', duration: 25, title: '', narrative: '', theoreticalHook: '', actionIds: [], hint: '' }] });

  return (
    <CollapsibleRow
      summary={
        <span className="flex items-center gap-3">
          <span className="w-6 h-6 rounded flex items-center justify-center text-[10px] font-mono font-bold shrink-0" style={{ background: `${theme.accent}15`, color: theme.accent }}>{index + 1}</span>
          {step.title || <span style={{ color: `${theme.ink}35` }}>Étape sans titre</span>}
        </span>
      }
      count={(step.actions || []).length}
      expanded={open}
      onToggle={() => setOpen(v => !v)}
      theme={theme}
    >
      <div className="space-y-5">
        {/* Titre + Problématisation */}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Titre de l'étape" required theme={theme}><Input value={step.title} onChange={e => onChange({ ...step, title: e.target.value })} placeholder="Analyse du besoin client" theme={theme} /></Field>
          <Field label="Titre client (clientTitle)" theme={theme} hint="Affiché en mode vente à la place du titre standard">
            <Input value={step.clientTitle || ''} onChange={e => onChange({ ...step, clientTitle: e.target.value })} placeholder="Ex : Un sujet qui va vous surprendre" theme={theme} />
          </Field>
          <Field label="Problématisation" theme={theme}><Input value={step.problematisation || ''} onChange={e => onChange({ ...step, problematisation: e.target.value })} placeholder="Question centrale…" theme={theme} /></Field>
        </div>
        <Field label="Description narrative" theme={theme}>
          <Textarea value={step.description || ''} onChange={e => onChange({ ...step, description: e.target.value })} placeholder="Contexte de l'étape, enjeux…" rows={2} theme={theme} />
        </Field>

        {/* Arc narratif */}
        <div className="grid grid-cols-3 gap-3">
          <Field label="Position dans l'arc" theme={theme}><Sel value={step.narrativeArc?.position || 'exposition'} onChange={e => onChange({ ...step, narrativeArc: { ...(step.narrativeArc || {}), position: e.target.value } })} options={ARC_POSITIONS} theme={theme} /></Field>
          <Field label="Tension" theme={theme}><Sel value={String(step.narrativeArc?.tensionLevel || 2)} onChange={e => onChange({ ...step, narrativeArc: { ...(step.narrativeArc || {}), tensionLevel: Number(e.target.value) } })} options={TENSION} theme={theme} /></Field>
          <Field label="Annonce de la prochaine étape" theme={theme}><Input value={step.narrativeArc?.nextStepForeshadow || ''} onChange={e => onChange({ ...step, narrativeArc: { ...(step.narrativeArc || {}), nextStepForeshadow: e.target.value } })} placeholder="L'objection finale se profile…" theme={theme} /></Field>
        </div>

        <Field label="Message de débrief (debriefMessage)" theme={theme} hint="Affiché à l'acheteur en fin de simulation">
          <Textarea value={step.debriefMessage || ''} onChange={e => onChange({ ...step, debriefMessage: e.target.value })} placeholder="Ex : Félicitations ! Le documentaire est sur votre grille." rows={2} theme={theme} />
        </Field>

        {/* Actions */}
        <div>
          <div className="flex items-center justify-between mb-2.5">
            <p className="text-[10px] font-mono tracking-[0.2em] uppercase" style={{ color: theme.accent }}>Actions ({(step.actions || []).length})</p>
            <Btn onClick={addAction} variant="accent" icon={Plus} small theme={theme}>Ajouter</Btn>
          </div>
          <div className="space-y-2">
            {(step.actions || []).map((action, aIdx) => (
              <ActionRow
                key={aIdx} action={action}
                onChange={updated => { const a = [...(step.actions || [])]; a[aIdx] = updated; onChange({ ...step, actions: a }); }}
                onRemove={() => onChange({ ...step, actions: (step.actions || []).filter((_, i) => i !== aIdx) })}
                theme={theme}
              />
            ))}
            {(step.actions || []).length === 0 && (
              <div className="py-6 text-center rounded-xl" style={{ background: `${theme.ink}03`, border: `1px dashed ${theme.ink}10` }}>
                <p className="text-[12px]" style={{ color: `${theme.ink}35`, fontFamily: '"DM Sans", sans-serif' }}>Aucune action. Ajoutez les décisions commerciales de cette étape.</p>
              </div>
            )}
          </div>
        </div>

        {/* Phases */}
        <div>
          <div className="flex items-center justify-between mb-2.5">
            <p className="text-[10px] font-mono tracking-[0.2em] uppercase" style={{ color: theme.accent }}>Phases ({(step.phases || []).length})</p>
            <Btn onClick={addPhase} variant="ghost" icon={Plus} small theme={theme}>Ajouter</Btn>
          </div>
          <div className="space-y-2">
            {(step.phases || []).map((phase, pIdx) => (
              <PhaseRow
                key={pIdx} phase={phase}
                stepActionIds={actionIds}
                onChange={updated => { const p = [...(step.phases || [])]; p[pIdx] = updated; onChange({ ...step, phases: p }); }}
                onRemove={() => onChange({ ...step, phases: (step.phases || []).filter((_, i) => i !== pIdx) })}
                theme={theme}
              />
            ))}
          </div>
        </div>

        <div className="flex justify-end pt-1">
          <Btn onClick={onRemove} variant="danger" icon={Trash2} small theme={theme}>Supprimer l'étape</Btn>
        </div>
      </div>
    </CollapsibleRow>
  );
};

// ─── Section : Solution (collapsible) ────────────────────────────────────────
const SolutionRow = ({ sol, onChange, onRemove, theme }) => {
  const [open, setOpen] = useState(!sol.name);

  const metricsStr = Object.entries(sol.expectedMetrics || {}).map(([k, v]) => `${k}:${v}`).join(', ');
  const parseMetrics = (raw) => { const o = {}; raw.split(',').forEach(p => { const [k, v] = p.split(':').map(s => s.trim()); if (k && !isNaN(v)) o[k] = Number(v); }); return o; };

  return (
    <CollapsibleRow
      summary={sol.name || <span style={{ color: `${theme.ink}35` }}>Scénario sans nom</span>}
      icon={Target}
      expanded={open}
      onToggle={() => setOpen(v => !v)}
      theme={theme}
      badge={sol.difficulty}
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Nom du scénario" required theme={theme}><Input value={sol.name} onChange={e => onChange({ ...sol, name: e.target.value })} placeholder="Accord commercial gagnant" theme={theme} /></Field>
          <Field label="Difficulté" theme={theme}>
            <Sel value={sol.difficulty || 'intermédiaire'} onChange={e => onChange({ ...sol, difficulty: e.target.value })} options={[{ value: 'facile', label: 'Accessible' }, { value: 'intermédiaire', label: 'Intermédiaire' }, { value: 'avancé', label: 'Avancé' }, { value: 'expert', label: 'Expert' }]} theme={theme} />
          </Field>
        </div>
        <Field label="Description" theme={theme}><Textarea value={sol.description || ''} onChange={e => onChange({ ...sol, description: e.target.value })} rows={2} theme={theme} /></Field>
        <Field label="Résumé affiché en fin de simulation" theme={theme}><Textarea value={sol.interpretation?.shortSummary || ''} onChange={e => onChange({ ...sol, interpretation: { ...(sol.interpretation || {}), shortSummary: e.target.value } })} rows={2} theme={theme} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Probabilité réelle (%)" theme={theme}><Input value={sol.interpretation?.realWorldProbability ?? 50} onChange={e => onChange({ ...sol, interpretation: { ...(sol.interpretation || {}), realWorldProbability: Number(e.target.value) } })} type="number" theme={theme} mono /></Field>
          <Field label="Probabilité théorique (%)" theme={theme}><Input value={sol.interpretation?.theoreticalPromise ?? 80} onChange={e => onChange({ ...sol, interpretation: { ...(sol.interpretation || {}), theoreticalPromise: Number(e.target.value) } })} type="number" theme={theme} mono /></Field>
        </div>
        <Field label="Actions requises (IDs, virgules)" theme={theme}><Input value={(sol.requiredActions || []).join(', ')} onChange={e => onChange({ ...sol, requiredActions: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} placeholder="ecoute_active, demo_live" theme={theme} mono /></Field>
        <Field label="Métriques attendues (cle:valeur, virgules)" theme={theme} hint="Même clés que les indicateurs de performance">
          <Input value={metricsStr} onChange={e => onChange({ ...sol, expectedMetrics: parseMetrics(e.target.value) })} placeholder="confiance_client:88, valeur_percue:90" theme={theme} mono />
        </Field>
        <div className="flex justify-end"><Btn onClick={onRemove} variant="danger" icon={Trash2} small theme={theme}>Supprimer</Btn></div>
      </div>
    </CollapsibleRow>
  );
};

// ─── Section : Contrefactuel (une ligne plate) ────────────────────────────────
const CfRow = ({ cf, onChange, onRemove, theme }) => (
  <div className="flex items-center gap-2 p-3 rounded-xl" style={{ background: `${theme.ink}04`, border: `1px solid ${theme.ink}08` }}>
    <Input value={cf.actionId} onChange={e => onChange({ ...cf, actionId: e.target.value })} placeholder="ID action" theme={theme} mono small />
    <Input value={cf.actionTitle} onChange={e => onChange({ ...cf, actionTitle: e.target.value })} placeholder="Titre" theme={theme} small />
    <Input value={cf.description} onChange={e => onChange({ ...cf, description: e.target.value })} placeholder="Impact si cette action avait été choisie" theme={theme} small />
    <input type="number" value={cf.deltaProbability} onChange={e => onChange({ ...cf, deltaProbability: Number(e.target.value) })} min={0} max={1} step={0.01} className="w-16 rounded text-right text-[11px] font-mono outline-none px-2 py-1" style={{ background: `${theme.ink}07`, border: `1px solid ${theme.ink}10`, color: theme.ink }} />
    <button onClick={onRemove} style={{ color: `${theme.ink}20` }} onMouseEnter={e => e.currentTarget.style.color = theme.error} onMouseLeave={e => e.currentTarget.style.color = `${theme.ink}20`}><Trash2 size={13} /></button>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// 6. ONGLETS PRINCIPAUX
// ═══════════════════════════════════════════════════════════════════════════════

const TAB_IDENTITY      = 'identity';
const TAB_STAKEHOLDERS  = 'stakeholders';
const TAB_AGENTS        = 'agents';
const TAB_INDICATORS    = 'indicators';
const TAB_STEPS         = 'steps';
const TAB_ENGINE        = 'engine';
const TAB_RESULTS       = 'results';
const TAB_PREVIEW       = 'preview';

const TABS = [
  { id: TAB_IDENTITY,     label: 'Identité',     icon: FileText  },
  { id: TAB_STAKEHOLDERS, label: 'Parties',      icon: Users     },
  { id: TAB_AGENTS,       label: 'Agents',       icon: User2     },
  { id: TAB_INDICATORS,   label: 'Indicateurs',  icon: BarChart2 },
  { id: TAB_STEPS,        label: 'Étapes',       icon: Layers    },
  { id: TAB_ENGINE,       label: 'Moteur',       icon: Settings  },
  { id: TAB_RESULTS,      label: 'Résultats',    icon: Target    },
  { id: TAB_PREVIEW,      label: 'Aperçu',       icon: Eye       },
];

// ─── Onglet Moteur (simplifié + avancé rétractable) ───────────────────────────
const TabEngine = ({ scenario, update, updateField, theme }) => {
  const [helpSent, setHelpSent] = useState(false);
  const [showAdv, setShowAdv] = useState(false);
  const eng = scenario.simulationEngine || {};
  const dynamics = eng.dynamics || [];

  return (
    <div className="space-y-5">
      {/* Bandeau info */}
      <div className="flex items-start gap-3 p-4 rounded-xl" style={{ background: `${theme.accent}06`, border: `1px solid ${theme.accent}18` }}>
        <AlertCircle size={16} style={{ color: theme.accent, flexShrink: 0, marginTop: 2 }} />
        <div className="flex-1">
          <p className="text-[13px] leading-relaxed" style={{ color: `${theme.ink}65`, fontFamily: '"DM Sans", sans-serif' }}>
            Le moteur gère les dynamiques paramétriques et la calibration automatique.
            Pour les scénarios complexes, notre équipe peut le configurer pour vous.
          </p>
        </div>
        <Btn onClick={() => setHelpSent(true)} variant="accent" icon={Network} small theme={theme} disabled={helpSent}>
          {helpSent ? 'Demande envoyée' : 'Aide technique'}
        </Btn>
      </div>

      {helpSent && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl" style={{ background: `${theme.success}08`, border: `1px solid ${theme.success}25` }}>
          <CheckCircle2 size={14} style={{ color: theme.success }} />
          <p className="text-[12px]" style={{ color: `${theme.ink}60`, fontFamily: '"DM Sans", sans-serif' }}>Demande transmise. L'équipe vous recontactera pour configurer le moteur.</p>
        </div>
      )}

      {/* Options de base */}
      <div className="grid grid-cols-2 gap-3">
        {[['predictionModel.enabled', 'Prédiction bayésienne'], ['calibration.enabled', 'Calibration génétique auto']].map(([path, label]) => {
          const keys = path.split('.');
          const val = keys.reduce((o, k) => o?.[k], eng) ?? false;
          return (
            <label key={path} className="flex items-center gap-3 p-3 rounded-xl cursor-pointer" style={{ background: `${theme.ink}04`, border: `1px solid ${theme.ink}08` }}>
              <input type="checkbox" checked={!!val} onChange={e => update(`simulationEngine.${path}`, e.target.checked)} />
              <span className="text-[12px]" style={{ color: `${theme.ink}65`, fontFamily: '"DM Sans", sans-serif' }}>{label}</span>
            </label>
          );
        })}
      </div>

      {/* Citations */}
      <Field label="Citations (une par ligne)" theme={theme} hint="Affichées dans l'overlay pendant les phases de narration">
        <Textarea value={(eng.quotes || []).join('\n')} onChange={e => update('simulationEngine.quotes', e.target.value.split('\n').filter(Boolean))} rows={3} placeholder={'"La confiance se construit en actes, pas en promesses."'} theme={theme} />
      </Field>

      <Field label="Citations théoriques (JSON)" theme={theme} hint="Tableau d'objets { actionId, text, author }">
  <Textarea
    value={JSON.stringify(scenario.initialState?.theory?.quotes || [], null, 2)}
    onChange={e => {
      try {
        const quotes = JSON.parse(e.target.value);
        updateField('initialState.theory.quotes', quotes);
      } catch { /* ignore */ }
    }}
    rows={6}
    theme={theme}
  />
</Field>

      {/* Dynamiques — rétractables */}
      <CollapsibleRow
        summary={`Dynamiques paramétriques`}
        icon={Activity}
        count={dynamics.length}
        expanded={showAdv}
        onToggle={() => setShowAdv(v => !v)}
        theme={theme}
        badge="Avancé"
      >
        <div className="space-y-3">
          <p className="text-[11px] leading-relaxed" style={{ color: `${theme.ink}50`, fontFamily: '"DM Sans", sans-serif' }}>
            Chaque dynamique définit comment un indicateur évolue à chaque tick de simulation.
            Les formules peuvent référencer d'autres indicateurs et des variables computées.
          </p>
          {dynamics.map((dyn, i) => (
            <div key={i} className="p-4 rounded-xl space-y-3" style={{ background: `${theme.ink}03`, border: `1px solid ${theme.ink}07` }}>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Paramètre" theme={theme}><Input value={dyn.parameter} onChange={e => { const d = [...dynamics]; d[i] = { ...d[i], parameter: e.target.value }; update('simulationEngine.dynamics', d); }} placeholder="confiance_client" theme={theme} mono /></Field>
                <Field label="Libellé rapport" theme={theme}><Input value={dyn.reportLabel || ''} onChange={e => { const d = [...dynamics]; d[i] = { ...d[i], reportLabel: e.target.value }; update('simulationEngine.dynamics', d); }} placeholder="Confiance client" theme={theme} /></Field>
              </div>
              <Field label="Formule" theme={theme} hint="Référencer les indicateurs par leur clé exacte">
                <Input value={dyn.formula} onChange={e => { const d = [...dynamics]; d[i] = { ...d[i], formula: e.target.value }; update('simulationEngine.dynamics', d); }} placeholder="0.7 * confiance_client + 0.3 * satisfaction_globale" theme={theme} mono />
              </Field>
              <div className="grid grid-cols-3 gap-3">
                {[['fallback', 'Défaut'], ['clampMin', 'Min'], ['clampMax', 'Max']].map(([k, l]) => (
                  <Field key={k} label={l} theme={theme}><Input value={dyn[k] ?? 50} onChange={e => { const d = [...dynamics]; d[i] = { ...d[i], [k]: Number(e.target.value) }; update('simulationEngine.dynamics', d); }} type="number" theme={theme} mono /></Field>
                ))}
              </div>
              <div className="flex justify-end"><Btn onClick={() => update('simulationEngine.dynamics', dynamics.filter((_, idx) => idx !== i))} variant="danger" icon={Trash2} small theme={theme}>Supprimer</Btn></div>
            </div>
          ))}
          <Btn onClick={() => update('simulationEngine.dynamics', [...dynamics, { parameter: '', formula: '', fallback: 50, clampMin: 0, clampMax: 100, type: 'linear', reportLabel: '' }])} variant="ghost" icon={Plus} small theme={theme}>Ajouter une dynamique</Btn>
        </div>
      </CollapsibleRow>
    </div>
  );
};

// ─── Onglet Résultats ─────────────────────────────────────────────────────────
const TabResults = ({ scenario, update, theme }) => {
  const solutions      = scenario.solutions || [];
  const counterfactuals = scenario.counterfactuals || [];

  return (
    <div className="space-y-8">
      {/* Solutions */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] font-mono tracking-[0.2em] uppercase" style={{ color: theme.accent }}>Scénarios de résultat — {solutions.length}</p>
          <Btn onClick={() => update('solutions', [...solutions, { name: '', description: '', requiredActions: [], expectedMetrics: {}, difficulty: 'intermédiaire', interpretation: { shortSummary: '', realWorldProbability: 50, theoreticalPromise: 80 } }])} variant="accent" icon={Plus} small theme={theme}>Ajouter</Btn>
        </div>
        <div className="space-y-2">
          {solutions.map((sol, i) => (
            <SolutionRow
              key={i} sol={sol}
              onChange={updated => { const s = [...solutions]; s[i] = updated; update('solutions', s); }}
              onRemove={() => update('solutions', solutions.filter((_, idx) => idx !== i))}
              theme={theme}
            />
          ))}
          {solutions.length === 0 && (
            <div className="py-8 text-center rounded-xl" style={{ background: `${theme.ink}03`, border: `1px dashed ${theme.ink}10` }}>
              <Target size={20} style={{ color: `${theme.ink}18`, margin: '0 auto 8px' }} />
              <p className="text-[12px]" style={{ color: `${theme.ink}35`, fontFamily: '"DM Sans", sans-serif' }}>Définissez les scénarios de résultat possibles.</p>
            </div>
          )}
        </div>
      </div>

      {/* Contrefactuels — rétractables si > 2 */}
      <CollapsibleRow
        summary="Analyses contrefactuelles"
        icon={BarChart2}
        count={counterfactuals.length}
        expanded={counterfactuals.length <= 2}
        onToggle={() => {}}
        theme={theme}
        autoCollapse
      >
        <div className="space-y-2">
          {counterfactuals.map((cf, i) => (
            <CfRow key={i} cf={cf}
              onChange={updated => { const c = [...counterfactuals]; c[i] = updated; update('counterfactuals', c); }}
              onRemove={() => update('counterfactuals', counterfactuals.filter((_, idx) => idx !== i))}
              theme={theme}
            />
          ))}
          <Btn onClick={() => update('counterfactuals', [...counterfactuals, { actionId: '', actionTitle: '', description: '', deltaProbability: 0.3 }])} variant="ghost" icon={Plus} small theme={theme}>Ajouter</Btn>
        </div>
      </CollapsibleRow>
    </div>
  );
};

// ─── Section : Agent (une ligne) ──────────────────────────────────────────
const AgentRow = ({ agent, onUpdate, onRemove, theme }) => {
  const [open, setOpen] = useState(!agent.name);

  // Gestion des réactions (objet clé-valeur)
  const reactionsStr = Object.entries(agent.reactionsTo || {}).map(([k, v]) => `${k}: ${v}`).join('\n');
  const parseReactions = (raw) => {
    const obj = {};
    raw.split('\n').forEach(line => {
      const [k, ...rest] = line.split(':');
      if (k && rest.length) obj[k.trim()] = rest.join(':').trim();
    });
    return obj;
  };

  return (
    <CollapsibleRow
      summary={agent.name || <span style={{ color: `${theme.ink}35` }}>Agent sans nom</span>}
      icon={Users}
      expanded={open}
      onToggle={() => setOpen(v => !v)}
      theme={theme}
      badge={agent.type || 'agent'}
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Nom" required theme={theme}>
            <Input value={agent.name} onChange={e => onUpdate('name', e.target.value)} placeholder="Ex : M. le Directeur" theme={theme} />
          </Field>
          <Field label="Nom d'affichage (narrativeName)" theme={theme}>
            <Input value={agent.narrativeName || ''} onChange={e => onUpdate('narrativeName', e.target.value)} placeholder="Le Directeur" theme={theme} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Type" theme={theme}>
            <Input value={agent.type || ''} onChange={e => onUpdate('type', e.target.value)} placeholder="Ex : decisionnaire" theme={theme} />
          </Field>
          <Field label="Archétype" theme={theme}>
            <Input value={agent.archetype || ''} onChange={e => onUpdate('archetype', e.target.value)} placeholder="Ex : sage, critique" theme={theme} />
          </Field>
        </div>
        <Field label="Réactions (actionId: texte de réaction, une par ligne)" theme={theme} hint="Une ligne par action, ex : diffuser_bande_annonce: Superbe bande-annonce !">
          <Textarea
            value={reactionsStr}
            onChange={e => onUpdate('reactionsTo', parseReactions(e.target.value))}
            placeholder={`diffuser_bande_annonce: Très belle bande-annonce !\nnegocier: Je suis ouvert à la discussion.`}
            rows={4}
            theme={theme}
          />
        </Field>
        <button onClick={onRemove} className="text-red-400 text-sm">Supprimer l'agent</button>
      </div>
    </CollapsibleRow>
  );
};

// ─── Section : Agents ────────────────────────────────────────────────────────
const SectionAgents = ({ agents, updateField, theme }) => {
  const addAgent = () => {
    const newAgents = [...(agents || []), { name: '', narrativeName: '', type: '', archetype: '', reactionsTo: {} }];
    updateField('initialState.agents', newAgents);
  };

  const updateAgent = (index, key, value) => {
    const updated = [...(agents || [])];
    updated[index] = { ...updated[index], [key]: value };
    updateField('initialState.agents', updated);
  };

  const removeAgent = (index) => {
    updateField('initialState.agents', (agents || []).filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-1">
        <p className="text-[10px] font-mono tracking-[0.2em] uppercase" style={{ color: theme.accent }}>
          Agents — {(agents || []).length}
        </p>
        <Btn onClick={addAgent} variant="accent" icon={Plus} small theme={theme}>Ajouter un agent</Btn>
      </div>
      {(agents || []).map((agent, i) => (
        <AgentRow
          key={i}
          agent={agent}
          onUpdate={(k, v) => updateAgent(i, k, v)}
          onRemove={() => removeAgent(i)}
          theme={theme}
        />
      ))}
      {(agents || []).length === 0 && (
        <div className="py-8 text-center rounded-xl" style={{ background: `${theme.ink}03`, border: `1px dashed ${theme.ink}10` }}>
          <Users size={20} style={{ color: `${theme.ink}18`, margin: '0 auto 8px' }} />
          <p className="text-[12px]" style={{ color: `${theme.ink}35` }}>Aucun agent. Ajoutez des personnages qui réagiront aux actions du client.</p>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// 7. COMPOSANT PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════

export default function CommercialCreator({ slug, onLaunch, onCancel,  }) {
  
  const { load, save } = useBuilderPersistence(slug);
  const { loadBuilderScenario } = usePesa();
  const navigate = useNavigate();

  const [scenario, setScenario] = useState(() => load() || JSON.parse(JSON.stringify(EMPTY)));
  const [activeTab, setActiveTab] = useState(TAB_IDENTITY);
  const [saveStatus, setSaveStatus] = useState('idle');
  const [showTemplates, setShowTemplates] = useState(false);
  const fileInputRef = useRef(null);

  const { theme, themeKey, toggle: toggleTheme } = useCommercialTheme(scenario);

  // Autosave
  useEffect(() => {
    save(scenario);
    setSaveStatus('saved');
    const t = setTimeout(() => setSaveStatus('idle'), 1400);
    return () => clearTimeout(t);
  }, [scenario]);

  // Mise à jour par chemin dotté
  const updateField = useCallback((path, value) => {
    setScenario(prev => {
      const clone = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let ref = clone;
      for (let i = 0; i < keys.length - 1; i++) {
        if (ref[keys[i]] === undefined) ref[keys[i]] = {};
        ref = ref[keys[i]];
      }
      ref[keys[keys.length - 1]] = value;
      return clone;
    });
  }, []);

  // Raccourci top-level
  const set = (key, value) => setScenario(prev => ({ ...prev, [key]: value }));

  // Helpers familles / params
  const families   = scenario.initialState?.families || [];
  const parameters = scenario.initialState?.parameters || {};
  const agents = scenario.initialState?.agents || [];

  const [newParamKey, setNewParamKey] = useState('');
  const [newParamVal, setNewParamVal] = useState(50);

  const addParam = () => {
    if (!newParamKey.trim()) return;
    const k = newParamKey.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    updateField('initialState.parameters', { ...parameters, [k]: Number(newParamVal) });
    setNewParamKey('');
    setNewParamVal(50);
  };

  const handleLaunch = (scenario) => {
    loadBuilderScenario(scenario);          // charge dans le store PESA
    navigate('/simulateur/commercial-sales/preview');
  };

  // Export / Import
  const handleExport = () => {
    const blob = new Blob([JSON.stringify(scenario, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `scenario_${scenario.slug || 'draft'}.json`;
    a.click();
  };
  const handleImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => { try { setScenario(JSON.parse(ev.target.result)); } catch { alert('Fichier JSON invalide.'); } };
    reader.readAsText(file);
    e.target.value = '';
  };

  const isValid = scenario.title.trim() && scenario.slug.trim() && scenario.steps.length > 0;

  // ── Validation per-tab ────────────────────────────────────────────────────
  const tabValid = {
    [TAB_IDENTITY]:     !!scenario.title && !!scenario.slug,
    [TAB_STAKEHOLDERS]: families.length > 0 && families.every(f => f.name.trim()),
    [TAB_AGENTS]:       agents.length > 0 && agents.every(a => a.name.trim()),
    [TAB_INDICATORS]:   Object.keys(parameters).length > 0,
    [TAB_STEPS]:        scenario.steps.length > 0 && scenario.steps.every(s => s.title.trim()),
    [TAB_ENGINE]:       true,
    [TAB_RESULTS]:      (scenario.solutions || []).length > 0,
    [TAB_PREVIEW]:      true,
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: theme.bgHex, color: theme.ink, fontFamily: '"DM Sans", sans-serif' }}>
      {/* Grille décorative */}
      <div className="fixed inset-0 pointer-events-none z-0" style={{ backgroundImage: `linear-gradient(${theme.gridLine} 1px, transparent 1px), linear-gradient(90deg, ${theme.gridLine} 1px, transparent 1px)`, backgroundSize: '80px 80px' }} />

      {/* ── HEADER ────────────────────────────────────────────────────────── */}
<header
  className="sticky top-0 z-50 shrink-0 backdrop-blur-xl"
  style={{ background: theme.headerBg, borderBottom: `1px solid ${theme.borderHex}` }}
>
  <div className="max-w-7xl mx-auto px-6 h-14 flex items-center gap-4">

    {/* ── Bouton de sortie ──────────────────────────────────────────── */}
    <button
      onClick={onCancel}
      className="relative group p-2 overflow-hidden rounded-sm border transition-all active:scale-95 shrink-0"
      style={{
        borderColor: `${theme.accent}30`,
        background: `${theme.accent}05`,
        color: theme.accent,
      }}
      onMouseEnter={e => {
        e.currentTarget.style.background = theme.accent;
        e.currentTarget.style.color = '#fff';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.background = `${theme.accent}05`;
        e.currentTarget.style.color = theme.accent;
      }}
      title="Fermer l'éditeur"
    >
      <X size={18} strokeWidth={2.5} />
    </button>

    {/* ── Signature système ─────────────────────────────────────────── */}
    <div className="flex items-center gap-3 shrink-0">
      <div className="relative group">
        <div className="absolute -inset-1 bg-amber-500 rounded blur opacity-20 group-hover:opacity-40 transition duration-1000"></div>
        <div
          className="relative p-2 border border-amber-500/30 rounded-sm"
          style={{ background: theme.surfaceHex }}
        >
          <FilePlus size={18} className="text-amber-500" />
        </div>
      </div>

      <div className="flex flex-col">
        <h1 className="text-sm font-black tracking-[0.2em] uppercase leading-none" style={{ color: theme.text }}>
          PESA<span className="text-amber-500">_</span>CORE
          <span className="text-amber-500">_</span>ARCHITECT
        </h1>
        <div className="flex items-center gap-3 mt-1.5">
          <div className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
            <span className="text-[12px] font-mono font-bold text-amber-600 uppercase tracking-tighter">
              System_Ready
            </span>
          </div>
          <span className="text-[12px] font-mono tracking-widest uppercase" style={{ color: theme.textMuted }}>
            v.{scenario.metadata?.versionEngine || '1.0.0'}
          </span>
        </div>
      </div>
    </div>

    {/* ── Titre du scénario (éditable) ────────────────────────────────── */}
    <div className="flex-1 min-w-0">
      <input
        value={scenario.title || ''}
        onChange={e => set('title', e.target.value)}
        placeholder="Titre du scénario..."
        className="w-full bg-transparent border-b border-transparent hover:border-amber-500/30 focus:border-amber-500 outline-none text-sm font-medium py-1 px-2 transition-colors truncate"
        style={{ color: theme.text }}
      />
    </div>

    {/* ── Sélecteur de templates ─────────────────────────────────────── */}
    <div className="relative group shrink-0 w-56">
      <div className="absolute left-3 inset-y-0 flex items-center pointer-events-none text-amber-500/50 group-hover:text-amber-500 transition-colors">
        <Layers size={14} />
      </div>
      <select
        onChange={(e) => {
          const value = e.target.value;
          if (value === 'empty') {
            setScenario(JSON.parse(JSON.stringify(EMPTY)));
            toast.success('Scénario vidé');
          } else if (value) {
            const tpl = TEMPLATES[value];
            if (tpl) {
              setScenario(JSON.parse(JSON.stringify(tpl)));
              toast.success('Modèle chargé');
            }
          }
        }}
        defaultValue=""
        className="w-full pl-10 pr-10 py-2 border rounded-sm text-[12px] font-mono uppercase tracking-widest outline-none transition-all appearance-none cursor-pointer truncate"
        style={{
          background: theme.surfaceHex,
          borderColor: theme.borderHex,
          color: theme.text,
        }}
        onFocus={e => {
          e.currentTarget.style.borderColor = `${theme.accent}50`;
          e.currentTarget.style.background = `${theme.accent}05`;
        }}
        onBlur={e => {
          e.currentTarget.style.borderColor = theme.borderHex;
          e.currentTarget.style.background = theme.surfaceHex;
        }}
      >
        <option value="" disabled>
          SELECT_TEMPLATE...
        </option>
        {Object.entries(TEMPLATES).map(([key, tpl]) => (
          <option key={key} value={key}>
            {tpl.title}
          </option>
        ))}
        <option value="empty">
          ── Vider (partir de zéro) ──
        </option>
      </select>
      <div className="absolute right-3 inset-y-0 flex items-center pointer-events-none" style={{ color: theme.textMuted }}>
        <ChevronDown size={14} />
      </div>
    </div>

    {/* ── Bascule de thème ──────────────────────────────────────────── */}
    <button
      onClick={toggleTheme}
      className="p-2 rounded-sm border transition-all hover:bg-white/5 shrink-0"
      style={{ borderColor: `${theme.accent}30`, color: theme.accent }}
      title="Basculer le thème"
    >
      {themeKey === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
    </button>

    {/* ── Actions (importer, exporter, lancer) ────────────────────────── */}
    <div className="flex items-center gap-2 shrink-0">
      <AnimatePresence>
        {saveStatus === 'saved' && (
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="text-[10px] font-mono"
            style={{ color: theme.textMuted }}
          >
            Sauvegardé
          </motion.span>
        )}
      </AnimatePresence>

      <label>
        <Btn variant="ghost" icon={Upload} small theme={theme} onClick={() => fileInputRef.current?.click()}>
          Importer
        </Btn>
        <input ref={fileInputRef} type="file" accept=".json" onChange={handleImport} className="hidden" />
      </label>

      <Btn onClick={handleExport} variant="ghost" icon={Download} small theme={theme}>
        Exporter
      </Btn>

      <Btn
        onClick={() => isValid && handleLaunch(scenario)}
        variant="primary"
        icon={Play}
        theme={theme}
        disabled={!isValid}
      >
        {isValid ? 'Lancer' : 'Incomplet'}
      </Btn>
    </div>
  </div>
</header>
      

      {/* ── CONTENU ─────────────────────────────────────────────────────────── */}
      <div className="relative z-10 flex-1 flex max-w-7xl mx-auto w-full">

        {/* Sidebar */}
        <nav className="w-44 shrink-0 py-5 px-3 flex flex-col gap-0.5" style={{ borderRight: `1px solid ${theme.borderHex}` }}>
          {TABS.map(tab => {
            const active = activeTab === tab.id;
            const valid  = tabValid[tab.id];
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left transition-all"
                style={{ background: active ? `${theme.accent}12` : 'transparent', color: active ? theme.accent : `${theme.ink}50`, border: `1px solid ${active ? `${theme.accent}25` : 'transparent'}` }}
                onMouseEnter={e => { if (!active) { e.currentTarget.style.background = `${theme.ink}04`; e.currentTarget.style.color = `${theme.ink}75`; }}}
                onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = `${theme.ink}50`; }}}
              >
                <tab.icon size={13} />
                <span className="text-[12px] font-medium flex-1">{tab.label}</span>
                <StatusDot ok={valid} theme={theme} />
              </button>
            );
          })}

          {/* Récapitulatif validation */}
          <div className="mt-auto pt-5">
            <div className="px-3 py-3 rounded-xl" style={{ background: isValid ? `${theme.success}08` : `${theme.ink}04`, border: `1px solid ${isValid ? `${theme.success}20` : `${theme.ink}08`}` }}>
              <div className="flex items-center gap-2 mb-1.5">
                {isValid ? <CheckCircle2 size={12} style={{ color: theme.success }} /> : <AlertCircle size={12} style={{ color: `${theme.ink}30` }} />}
                <span className="text-[10px] font-mono" style={{ color: isValid ? theme.success : `${theme.ink}40` }}>{isValid ? 'Prêt' : 'Incomplet'}</span>
              </div>
              {!isValid && (
                <div className="space-y-0.5">
                  {!scenario.title.trim() && <p className="text-[9px] font-mono" style={{ color: `${theme.ink}35` }}>Titre manquant</p>}
                  {!scenario.slug.trim()  && <p className="text-[9px] font-mono" style={{ color: `${theme.ink}35` }}>Slug manquant</p>}
                  {scenario.steps.length === 0 && <p className="text-[9px] font-mono" style={{ color: `${theme.ink}35` }}>Aucune étape</p>}
                </div>
              )}
            </div>
          </div>
        </nav>

        {/* Zone principale */}
        <main className="flex-1 overflow-y-auto px-8 py-6" style={{ scrollbarWidth: 'none' }}>
          <AnimatePresence mode="wait">
            <motion.div key={activeTab} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }} transition={{ duration: 0.15 }}>

              {activeTab === TAB_IDENTITY && <SectionIdentity scenario={scenario} update={(k, v) => k.includes('.') ? updateField(k, v) : set(k, v)} theme={theme} themeKey={themeKey} />}

              {activeTab === TAB_STAKEHOLDERS && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-[10px] font-mono tracking-[0.2em] uppercase" style={{ color: theme.accent }}>Parties prenantes — {families.length}</p>
                    <Btn onClick={() => updateField('initialState.families', [...families, { name: '', isActive: true, isToxic: false, narrativeRole: '' }])} variant="accent" icon={Plus} small theme={theme}>Ajouter</Btn>
                  </div>
                  {families.map((fam, i) => (
                    <FamilyRow
                      key={i} fam={fam}
                      onUpdate={(k, v) => { const f = [...families]; f[i] = { ...f[i], [k]: v }; updateField('initialState.families', f); }}
                      onRemove={() => updateField('initialState.families', families.filter((_, idx) => idx !== i))}
                      theme={theme}
                    />
                  ))}
                </div>
              )}

              {activeTab === TAB_AGENTS && (
                <SectionAgents
                  agents={scenario.initialState?.agents || []}
                  updateField={updateField}
                  theme={theme}
                />
              )}

              {activeTab === TAB_INDICATORS && (
                <div className="space-y-4">
                  <p className="text-[10px] font-mono tracking-[0.2em] uppercase" style={{ color: theme.accent }}>Indicateurs initiaux — {Object.keys(parameters).length}</p>
                  <div className="space-y-2">
                    {Object.entries(parameters).map(([k, v]) => (
                      <IndicatorRow key={k} k={k} val={v} onUpdate={val => updateField('initialState.parameters', { ...parameters, [k]: val })} onRemove={() => { const p = { ...parameters }; delete p[k]; updateField('initialState.parameters', p); }} theme={theme} />
                    ))}
                  </div>
                  <div className="flex items-end gap-3 pt-2">
                    <div className="flex-1">
                      <Field label="Nom de l'indicateur" theme={theme} hint="sera converti en minuscules_tirets_bas">
                        <Input value={newParamKey} onChange={e => setNewParamKey(e.target.value)} placeholder="confiance_client" theme={theme} onKeyDown={e => e.key === 'Enter' && addParam()} mono />
                      </Field>
                    </div>
                    <div className="w-24">
                      <Field label="Valeur init." theme={theme}>
                        <Input value={newParamVal} onChange={e => setNewParamVal(e.target.value)} type="number" min={0} max={100} theme={theme} mono />
                      </Field>
                    </div>
                    <Btn onClick={addParam} variant="primary" icon={Plus} small theme={theme}>Ajouter</Btn>
                  </div>
                </div>
              )}

              {activeTab === TAB_STEPS && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-[10px] font-mono tracking-[0.2em] uppercase" style={{ color: theme.accent }}>Étapes — {scenario.steps.length}</p>
                    <Btn onClick={() => set('steps', [...scenario.steps, { title: '', description: '', problematisation: '', actions: [], causality: [], phases: [], narrativeArc: { position: 'exposition', tensionLevel: 2 } }])} variant="primary" icon={Plus} theme={theme}>Nouvelle étape</Btn>
                  </div>
                  {scenario.steps.length === 0 && (
                    <div className="py-12 text-center rounded-xl" style={{ background: `${theme.ink}03`, border: `1px dashed ${theme.ink}10` }}>
                      <Layers size={22} style={{ color: `${theme.ink}18`, margin: '0 auto 10px' }} />
                      <p className="text-[13px]" style={{ color: `${theme.ink}40` }}>Aucune étape. Définissez le déroulé de la simulation.</p>
                    </div>
                  )}
                  {scenario.steps.map((step, i) => (
                    <StepRow
                      key={i} step={step} index={i}
                      onChange={updated => { const s = [...scenario.steps]; s[i] = updated; set('steps', s); }}
                      onRemove={() => set('steps', scenario.steps.filter((_, idx) => idx !== i))}
                      theme={theme}
                    />
                  ))}
                </div>
              )}

              {activeTab === TAB_ENGINE    && <TabEngine    scenario={scenario} update={updateField} theme={theme} />}
              {activeTab === TAB_RESULTS   && <TabResults   scenario={scenario} update={(k, v) => set(k, v)} theme={theme} />}

              {activeTab === TAB_PREVIEW && (
                <div className="space-y-3">
                  <p className="text-[10px] font-mono tracking-[0.2em] uppercase" style={{ color: theme.accent }}>Aperçu JSON — {JSON.stringify(scenario).length} caractères</p>
                  <pre className="p-5 rounded-xl text-[11px] font-mono overflow-auto max-h-[65vh] leading-loose" style={{ background: `${theme.ink}04`, border: `1px solid ${theme.ink}08`, color: `${theme.ink}65` }}>
                    {JSON.stringify(scenario, null, 2)}
                  </pre>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
};